import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Box, Typography, CircularProgress } from '@mui/material';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { btnPrimarySx, btnSecondarySx } from '../../../utils/buttonSx';
import { useNavigate } from 'react-router-dom';
import { RootState } from '../../../redux/store';
import axios from '../../../interceptors/axios';
import { MediaFolderCard, MediaFileCard, GlassCheckbox, baseName, nameExt } from './MediaCard';
import ImageEditorPage from './ImageEditorPage';
import { usePageHeader } from '../../../context/PageHeaderContext';

// ── Types ──────────────────────────────────────────────────────────────────

interface MediaFolder {
  id: number;
  name: string;
  parent_id: number | null;
  file_count: number;
  created_at?: string;
  children?: MediaFolder[];
}

function suggestFolderName(base: string, existing: string[]): string {
  const lower = existing.map(n => n.toLowerCase());
  if (!lower.includes(base.toLowerCase())) return base;
  let i = 1;
  while (lower.includes(`${base} ${i}`.toLowerCase())) i++;
  return `${base} ${i}`;
}

interface MediaFile {
  id: number;
  original_name: string;
  mime_type: string;
  size_bytes: number;
  duration_ms: number | null;
  width: number | null;
  height: number | null;
  thumbnail_path: string | null;
  folder_id: number | null;
  last_played_at: string | null;
  created_at: string;
  default_duration_ms: number | null;
  round_duration: boolean;
  avail_period_enabled?: boolean;
  avail_from?: string | null;
  avail_to?: string | null;
  avail_sched_enabled?: boolean;
  avail_status?: 'active' | 'expired' | 'not_started';
  avail_has_schedule?: boolean;
  default_scale_mode?: string;
  video_codec?: string | null;
  audio_codec?: string | null;
  bit_depth?: number | null;
  transcoding?: boolean;
}

interface AvailSchedRow {
  id?: number;
  days_mask: number;
  from_time: string;
  to_time: string;
}

interface StorageStats {
  used_bytes: number;
  file_count: number;
  limit_bytes: number;
}

type ConflictAction = 'skip' | 'copy' | 'rename' | 'overwrite';

interface ConflictExisting {
  id: number;
  original_name: string;
  size_bytes: number;
  mime_type: string;
  created_at: string;
  width?: number | null;
  height?: number | null;
  duration_ms?: number | null;
  video_codec?: string | null;
  audio_codec?: string | null;
}

interface FileMeta { durationMs?: number; width?: number; height?: number; thumbnail?: Blob; videoCodec?: string; audioCodec?: string; }

interface ConflictItem {
  index: number;
  localFile: File;
  localMeta: FileMeta;
  existing: ConflictExisting;
  action: ConflictAction;
  newName: string;
}

function suggestCopyName(name: string, existing: string[]): string {
  const dot = name.lastIndexOf('.');
  const base = dot >= 0 ? name.slice(0, dot) : name;
  const ext = dot >= 0 ? name.slice(dot) : '';
  const lower = existing.map(n => n.toLowerCase());
  let i = 1;
  while (lower.includes(`${base} (${i})${ext}`.toLowerCase())) i++;
  return `${base} (${i})${ext}`;
}

// ── Helpers ────────────────────────────────────────────────────────────────

const fmtSize = (b: number) => `${Math.round(b / 1024).toLocaleString()} KB`;
const fmtSizeMB = (b: number) => `${(b / 1048576).toFixed(2)} MB`;
const fmtSizeGB = (b: number) => `${(b / 1073741824).toFixed(2)} GB`;

const MAX_FILE_BYTES = 100 * 1024 * 1024; // 100 MB per file

const fmtDur = (ms: number | null) => {
  if (!ms) return '—';
  const s = Math.round(ms / 1000);
  const m = Math.floor(s / 60);
  const rem = s % 60;
  return `${m}:${String(rem).padStart(2, '0')}`;
};

const fmtDurFull = (ms: number | null) => {
  if (!ms) return '—';
  const millis = ms % 1000;
  const totalSec = Math.floor(ms / 1000);
  const m = Math.floor(totalSec / 60);
  const s = totalSec % 60;
  return `${m}:${String(s).padStart(2, '0')}.${String(millis).padStart(3, '0')}`;
};

const fmtBitrate = (sizeBytes: number, durationMs: number | null): string | null => {
  if (!durationMs || durationMs === 0) return null;
  const kbps = Math.round((sizeBytes * 8) / (durationMs / 1000) / 1000);
  return `${kbps.toLocaleString()} kbps`;
};

// ── Lightweight video codec probe — no external deps ──────────────────────

const MP4_VIDEO_FOURCC: Record<string, string> = {
  avc1:'H.264', avc2:'H.264', avc3:'H.264', avc4:'H.264',
  hvc1:'H.265', hev1:'H.265', dvhe:'H.265', dvh1:'H.265',
  av01:'AV1', vp08:'VP8', vp09:'VP9', mp4v:'MPEG-4',
};
const MP4_AUDIO_FOURCC: Record<string, string> = {
  mp4a:'AAC', Opus:'Opus', opus:'Opus', flac:'FLAC',
  sowt:'PCM', lpcm:'PCM', 'ac-3':'AC-3', 'ec-3':'E-AC-3',
};

function readFourCC(view: DataView, offset: number): string {
  return String.fromCharCode(view.getUint8(offset), view.getUint8(offset+1), view.getUint8(offset+2), view.getUint8(offset+3));
}

interface CodecInfo { videoCodec?: string; audioCodec?: string }

function walkMP4(view: DataView, start: number, end: number, info: CodecInfo, depth = 0): void {
  if (depth > 8) return;
  const CONTAINERS = ['moov','trak','mdia','minf','stbl'];
  let pos = start;
  while (pos + 8 <= end) {
    const size = view.getUint32(pos);
    if (size < 8 || pos + size > end) break;
    const type = readFourCC(view, pos + 4);
    if (type === 'stsd') {
      const count = view.getUint32(pos + 12); // FullBox: +8 header + 4 ver/flags = +12
      let ep = pos + 16;
      for (let i = 0; i < Math.min(count, 8) && ep + 8 <= end; i++) {
        const eSize = view.getUint32(ep);
        const eType = readFourCC(view, ep + 4);
        if (!info.videoCodec && MP4_VIDEO_FOURCC[eType]) info.videoCodec = MP4_VIDEO_FOURCC[eType];
        if (!info.audioCodec && MP4_AUDIO_FOURCC[eType]) info.audioCodec = MP4_AUDIO_FOURCC[eType];
        if (eSize < 8) break;
        ep += eSize;
      }
    }
    if (CONTAINERS.includes(type)) walkMP4(view, pos + 8, pos + size, info, depth + 1);
    pos += size;
  }
}

async function probeVideoCodec(file: File): Promise<CodecInfo> {
  try {
    const buf = await file.slice(0, Math.min(2097152, file.size)).arrayBuffer();
    const u8 = new Uint8Array(buf);
    const view = new DataView(buf);
    // WebM/MKV — EBML magic: 1A 45 DF A3
    if (u8[0] === 0x1A && u8[1] === 0x45 && u8[2] === 0xDF && u8[3] === 0xA3) {
      const text = new TextDecoder('latin1').decode(u8.slice(0, Math.min(65536, u8.length)));
      const info: CodecInfo = {};
      for (const [id,name] of [['V_VP9','VP9'],['V_VP8','VP8'],['V_AV1','AV1'],['V_MPEG4/ISO/AVC','H.264'],['V_MPEGH/ISO/HEVC','H.265']] as [string,string][])
        if (text.includes(id)) { info.videoCodec = name; break; }
      for (const [id,name] of [['A_OPUS','Opus'],['A_VORBIS','Vorbis'],['A_AAC','AAC'],['A_FLAC','FLAC'],['A_MPEG/L3','MP3']] as [string,string][])
        if (text.includes(id)) { info.audioCodec = name; break; }
      return info;
    }
    // MP4/MOV
    if (view.byteLength >= 8) {
      const sig = readFourCC(view, 4);
      if (['ftyp','moov','mdat','free','skip','wide'].includes(sig)) {
        const info: CodecInfo = {};
        walkMP4(view, 0, buf.byteLength, info);
        return info;
      }
    }
  } catch { /* ignore */ }
  return {};
}

const fmtDurRounded = (ms: number | null) => {
  if (!ms) return '—';
  const rounded = Math.round(ms / 1000) * 1000;
  return fmtDur(rounded);
};

const fmtRelative = (d: string | null) => {
  if (!d) return '—';
  const diff = Math.round((Date.now() - new Date(d).getTime()) / 1000);
  if (diff < 60) return `${diff}s ago`;
  if (diff < 3600) return `${Math.round(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.round(diff / 3600)}h ago`;
  return new Date(d).toLocaleDateString();
};

const isVideo = (mime: string) => mime.startsWith('video/');
const isImage = (mime: string) => mime.startsWith('image/');

const availChip = (status?: string) => {
  if (status === 'expired') return { label: 'Expired', color: '#f87171', bg: 'rgba(248,113,113,.1)', dot: '#ef4444' };
  if (status === 'not_started') return { label: 'Not started', color: '#818cf8', bg: 'rgba(129,140,248,.1)', dot: '#6366f1' };
  return { label: 'Active', color: '#4ade80', bg: 'rgba(74,222,128,.1)', dot: '#22c55e' };
};

const computeFileStatus = (f: MediaFile): string => {
  if (f.avail_period_enabled === false) return 'active';
  const now = new Date();
  if (f.avail_from && new Date(f.avail_from) > now) return 'not_started';
  if (f.avail_to && new Date(f.avail_to) < now) return 'expired';
  return f.avail_status ?? 'active';
};

// ── SVG Icons ──────────────────────────────────────────────────────────────

const IcoFolder = ({ size = 16 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 16 16" fill="none">
    <path d="M1 4a1 1 0 011-1h3.586L7 4.5A1 1 0 007.7 4.75H14a1 1 0 011 1v6a1 1 0 01-1 1H2a1 1 0 01-1-1V4z" fill="var(--accent)" fillOpacity=".85"/>
  </svg>
);

const IcoSearch = () => (
  <svg width={15} height={15} viewBox="0 0 14 14" fill="none">
    <circle cx="6" cy="6" r="4.5" stroke="currentColor" strokeWidth="1.4"/>
    <path d="M9.5 9.5l2.5 2.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
  </svg>
);

const IcoUpload = ({ size = 15 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 14 14" fill="none">
    <path d="M7 1.5v8M4.5 4L7 1.5 9.5 4" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M1.5 10.5v1a1 1 0 001 1h9a1 1 0 001-1v-1" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
  </svg>
);

const IcoPlay = ({ size = 20 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 18 18" fill="none">
    <circle cx="9" cy="9" r="8" stroke="rgba(255,255,255,.7)" strokeWidth="1.2"/>
    <path d="M7.5 6.5l4.5 2.5-4.5 2.5V6.5z" fill="white"/>
  </svg>
);

const IcoImage = ({ size = 24 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 22 22" fill="none">
    <rect x="2" y="2" width="18" height="18" rx="3" stroke="currentColor" strokeWidth="1.4" strokeOpacity=".4"/>
    <circle cx="7.5" cy="7.5" r="1.5" fill="currentColor" fillOpacity=".5"/>
    <path d="M3 16l5-5 3.5 3.5 2-2 5.5 5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" strokeOpacity=".5"/>
  </svg>
);

const IcoDots = () => (
  <svg width={16} height={16} viewBox="0 0 16 16" fill="none">
    <circle cx="8" cy="4" r="1.2" fill="currentColor"/>
    <circle cx="8" cy="8" r="1.2" fill="currentColor"/>
    <circle cx="8" cy="12" r="1.2" fill="currentColor"/>
  </svg>
);

const IcoFilter = () => (
  <svg width={15} height={15} viewBox="0 0 14 14" fill="none">
    <path d="M1 2.5h12M3 7h8M5 11.5h4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
  </svg>
);

const IcoGrid = () => (
  <svg width={15} height={15} viewBox="0 0 14 14" fill="none">
    <rect x="1" y="1" width="5" height="5" rx="1" fill="currentColor" fillOpacity=".7"/>
    <rect x="8" y="1" width="5" height="5" rx="1" fill="currentColor" fillOpacity=".7"/>
    <rect x="1" y="8" width="5" height="5" rx="1" fill="currentColor" fillOpacity=".7"/>
    <rect x="8" y="8" width="5" height="5" rx="1" fill="currentColor" fillOpacity=".7"/>
  </svg>
);

const IcoList = () => (
  <svg width={15} height={15} viewBox="0 0 14 14" fill="none">
    <path d="M1 3h12M1 7h12M1 11h12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
  </svg>
);

const IcoTag = () => (
  <svg width={15} height={15} viewBox="0 0 14 14" fill="none">
    <path d="M2 2h4.5l5.5 5.5a2 2 0 010 2.83l-1.67 1.67a2 2 0 01-2.83 0L2 6.5V2z" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round"/>
    <circle cx="5" cy="5" r="1" fill="currentColor"/>
  </svg>
);

const IcoPlus = ({ size = 12 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 12 12" fill="none">
    <path d="M6 1v10M1 6h10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
  </svg>
);

const IcoDownload = () => (
  <svg width={14} height={14} viewBox="0 0 13 13" fill="none">
    <path d="M6.5 1v8M3.5 6l3 3 3-3" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M1 11h11" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
  </svg>
);

const IcoTrash = () => (
  <svg width={14} height={14} viewBox="0 0 13 13" fill="none">
    <path d="M2 3.5h9M5 3.5V2.5a.5.5 0 01.5-.5h2a.5.5 0 01.5.5v1M10 3.5l-.5 7a1 1 0 01-1 .9H4.5a1 1 0 01-1-.9L3 3.5" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const IcoPencil = () => (
  <svg width={14} height={14} viewBox="0 0 13 13" fill="none">
    <path d="M8.5 2l2.5 2.5-7 7H1.5V9L8.5 2z" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:3002/api';

// ── Video / Image Player ───────────────────────────────────────────────────

const MediaPlayer: React.FC<{ file: MediaFile; onClose: () => void }> = ({ file, onClose }) => {
  const iv = isVideo(file.mime_type);
  const src = `${API_BASE}/media/file/${file.id}`;

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <Box onClick={onClose} sx={{ position: 'fixed', inset: 0, zIndex: 600, bgcolor: 'rgba(0,0,0,.88)', backdropFilter: 'blur(10px)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <Box onClick={e => e.stopPropagation()} sx={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        {iv
          ? <video
              controls autoPlay src={src}
              style={{ maxWidth: '88vw', maxHeight: '84vh', borderRadius: 12, outline: 'none', display: 'block' }}
              onTimeUpdate={file.round_duration && file.duration_ms ? (e => {
                const vid = e.currentTarget;
                const limit = Math.round(file.duration_ms! / 1000);
                if (vid.currentTime >= limit) { vid.pause(); vid.currentTime = limit; }
              }) : undefined}
            />
          : <img src={src} alt={file.original_name} style={{ maxWidth: '88vw', maxHeight: '84vh', borderRadius: 12, objectFit: 'contain', display: 'block' }} />
        }
        <Box onClick={onClose} component="button" sx={{
          position: 'absolute', top: -42, right: 0,
          width: 32, height: 32, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
          bgcolor: 'rgba(255,255,255,.12)', border: '1px solid rgba(255,255,255,.2)', color: 'white', cursor: 'pointer',
          '&:hover': { bgcolor: 'rgba(255,255,255,.22)' },
        }}>
          <svg width={14} height={14} viewBox="0 0 12 12" fill="none"><path d="M2 2l8 8M10 2l-8 8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/></svg>
        </Box>
        <Typography sx={{ position: 'absolute', bottom: -30, left: 0, right: 0, textAlign: 'center', fontSize: 13, color: 'rgba(255,255,255,.5)', pointerEvents: 'none' }}>
          {file.original_name}
        </Typography>
      </Box>
    </Box>
  );
};

// ── Glass helper ───────────────────────────────────────────────────────────

const glassSx = {
  background: 'var(--glass-bg)',
  backdropFilter: 'blur(20px)',
  WebkitBackdropFilter: 'blur(20px)',
  border: '1px solid var(--glass-border)',
};

// ── Thumbnail ──────────────────────────────────────────────────────────────

const Thumb: React.FC<{ file: MediaFile; size?: number }> = ({ file, size = 42 }) => {
  const iv = isVideo(file.mime_type);
  const ii = isImage(file.mime_type);
  return (
    <Box sx={{ width: size, height: size, borderRadius: '7px', overflow: 'hidden', flexShrink: 0, position: 'relative', background: 'linear-gradient(145deg, var(--folder-card-from) 0%, var(--folder-card-to) 100%)', border: '1px solid var(--glass-border)' }}>
      {(ii || file.thumbnail_path) && (
        <img
          src={`${API_BASE}/media/thumb/${file.id}`}
          alt=""
          style={{ width: '100%', height: '100%', objectFit: 'cover', position: 'absolute', inset: 0 }}
          onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }}
        />
      )}
      {iv && <Box sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}><IcoPlay size={size < 40 ? 14 : 20} /></Box>}
    </Box>
  );
};

// ── Folder thumbnail (in list) ─────────────────────────────────────────────

const FolderThumb: React.FC<{ size?: number }> = ({ size = 42 }) => (
  <Box sx={{ width: size, height: size, borderRadius: '7px', flexShrink: 0, background: 'linear-gradient(135deg, rgba(var(--accent-rgb,99,102,241),.18), rgba(var(--accent-rgb,99,102,241),.08))', border: '1px solid var(--glass-border)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
    <IcoFolder size={Math.round(size * 0.64)} />
  </Box>
);

// ── File preview icons ──────────────────────────────────────────────────────

const IcoPause: React.FC<{ size?: number }> = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="white">
    <rect x="6" y="4" width="4" height="16" rx="1" />
    <rect x="14" y="4" width="4" height="16" rx="1" />
  </svg>
);

const IcoExpand: React.FC<{ size?: number }> = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round">
    <polyline points="15 3 21 3 21 9" />
    <polyline points="9 21 3 21 3 15" />
    <line x1="21" y1="3" x2="14" y2="10" />
    <line x1="3" y1="21" x2="10" y2="14" />
  </svg>
);

// ── Inline file preview (video plays in place) ───────────────────────────────

// ── Video popup modal ──────────────────────────────────────────────────────

const fmtSeconds = (s: number) => `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, '0')}`;

const VideoPopupModal: React.FC<{ file: MediaFile; onClose: () => void }> = ({ file, onClose }) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const iv = isVideo(file.mime_type);
  const [playing, setPlaying] = useState(iv);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);

  const toggle = () => {
    if (!videoRef.current) return;
    if (playing) { videoRef.current.pause(); setPlaying(false); }
    else { videoRef.current.play().catch(() => {}); setPlaying(true); }
  };

  const seek = (e: React.ChangeEvent<HTMLInputElement>) => {
    const t = Number(e.target.value);
    if (videoRef.current) videoRef.current.currentTime = t;
    setCurrentTime(t);
  };

  const modal = (
    <Box
      onClick={onClose}
      sx={{ position: 'fixed', inset: 0, zIndex: 99999, bgcolor: 'rgba(0,0,0,.75)', backdropFilter: 'blur(10px)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
    >
      <Box
        onClick={e => e.stopPropagation()}
        sx={{ width: '80vw', maxWidth: 1100, borderRadius: '12px', overflow: 'hidden', background: '#0b0f1a', border: '1px solid rgba(255,255,255,.1)', boxShadow: '0 24px 80px rgba(0,0,0,.9)', display: 'flex', flexDirection: 'column', position: 'relative' }}
      >
        {/* Close button */}
        <Box component="button" onClick={onClose} sx={{ position: 'absolute', top: 10, right: 10, zIndex: 1, width: 30, height: 30, borderRadius: '8px', bgcolor: 'rgba(0,0,0,.55)', border: '1px solid rgba(255,255,255,.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: '#fff', '&:hover': { bgcolor: 'rgba(0,0,0,.8)' } }}>
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
        </Box>

        {/* Media area */}
        <Box sx={{ bgcolor: '#000', aspectRatio: '16/9' }}>
          {iv ? (
            <video
              ref={videoRef}
              src={`${API_BASE}/media/file/${file.id}`}
              autoPlay
              style={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block' }}
              onTimeUpdate={() => setCurrentTime(videoRef.current?.currentTime ?? 0)}
              onLoadedMetadata={() => setDuration(videoRef.current?.duration ?? 0)}
              onEnded={() => setPlaying(false)}
            />
          ) : (
            <img src={`${API_BASE}/media/thumb/${file.id}`} alt="" style={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block' }} />
          )}
        </Box>

        {/* Controls — video only */}
        {iv && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px', px: '14px', py: '10px', background: 'rgba(255,255,255,.03)', borderTop: '1px solid rgba(255,255,255,.07)' }}>
            <Box component="button" onClick={toggle} sx={{ width: 32, height: 32, borderRadius: '8px', bgcolor: 'var(--accent)', border: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', flexShrink: 0, '&:hover': { opacity: .88 } }}>
              {playing
                ? <IcoPause size={14} />
                : <Box sx={{ ml: '2px', width: 0, height: 0, borderTop: '7px solid transparent', borderBottom: '7px solid transparent', borderLeft: '12px solid white' }} />
              }
            </Box>
            <Box component="input" type="range" min={0} max={duration || 1} step={0.1} value={currentTime} onChange={seek}
              sx={{ flex: 1, accentColor: 'var(--accent)', cursor: 'pointer', height: 4 }}
            />
            <Typography sx={{ fontSize: 12, color: 'var(--text-secondary)', fontVariantNumeric: 'tabular-nums', flexShrink: 0, minWidth: 80, textAlign: 'right' }}>
              {fmtSeconds(currentTime)} / {fmtSeconds(duration)}
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );

  return createPortal(modal, document.body);
};

// ── File preview (detail panel) ────────────────────────────────────────────

const FilePreview: React.FC<{ file: MediaFile; onEdit?: () => void }> = ({ file, onEdit }) => {
  const [popupOpen, setPopupOpen] = useState(false);
  const iv = isVideo(file.mime_type);
  const chip = availChip(computeFileStatus(file));

  return (
    <>
      <Box sx={{ aspectRatio: '16/9', background: 'linear-gradient(145deg, var(--folder-card-from) 0%, var(--folder-card-to) 100%)', overflow: 'hidden', position: 'relative', borderRadius: '8px',
        '&:hover .preview-ctrl': { opacity: 1 },
      }}>
        {(isImage(file.mime_type) || file.thumbnail_path) && (
          <img
            src={`${API_BASE}/media/thumb/${file.id}`}
            alt=""
            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
            onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }}
          />
        )}
        {/* Play/open button — always opens popup */}
        <Box onClick={() => setPopupOpen(true)} sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', bgcolor: 'rgba(0,0,0,.12)', '&:hover': { bgcolor: 'rgba(0,0,0,.22)' } }}>
          <Box sx={{ width: 52, height: 52, borderRadius: '50%', bgcolor: 'rgba(255,255,255,.14)', border: '1.5px solid rgba(255,255,255,.38)', backdropFilter: 'blur(8px)', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'transform .15s', '&:hover': { transform: 'scale(1.09)', bgcolor: 'rgba(255,255,255,.22)' } }}>
            {iv
              ? <Box sx={{ ml: '3px', width: 0, height: 0, borderTop: '10px solid transparent', borderBottom: '10px solid transparent', borderLeft: '18px solid white' }} />
              : <IcoExpand size={20} />
            }
          </Box>
        </Box>
        <Box sx={{ position: 'absolute', top: 6, left: 6, display: 'flex', alignItems: 'center', gap: '4px', pointerEvents: 'none' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: 10, fontWeight: 600, px: '7px', py: '2px', borderRadius: 20, bgcolor: chip.bg, color: chip.color, backdropFilter: 'blur(8px)', border: `1px solid ${chip.color}22` }}>
            <Box sx={{ width: 4, height: 4, borderRadius: '50%', bgcolor: chip.dot, boxShadow: `0 0 4px ${chip.dot}` }} />
            {chip.label}
          </Box>
          {file.avail_has_schedule && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: 10, fontWeight: 600, px: '7px', py: '2px', borderRadius: 20, bgcolor: 'rgba(251,191,36,.12)', color: '#fbbf24', backdropFilter: 'blur(8px)', border: '1px solid rgba(251,191,36,.25)' }}>
              Scheduled
            </Box>
          )}
        </Box>
        {/* Pencil edit button — images only */}
        {!iv && onEdit && (
          <Box onClick={e => { e.stopPropagation(); onEdit(); }} title="Edit image"
            sx={{ position: 'absolute', bottom: 6, right: 6, width: 30, height: 30, borderRadius: '8px', bgcolor: 'rgba(0,0,0,.5)', border: '1px solid rgba(255,255,255,.25)', backdropFilter: 'blur(8px)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', '&:hover': { bgcolor: 'rgba(91,141,238,.6)', borderColor: 'rgba(91,141,238,.8)' } }}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"/>
              <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"/>
            </svg>
          </Box>
        )}
      </Box>
      {popupOpen && <VideoPopupModal file={file} onClose={() => setPopupOpen(false)} />}
    </>
  );
};

// ── Pill toggle ────────────────────────────────────────────────────────────

const PillToggle: React.FC<{ checked: boolean; onChange: (v: boolean) => void }> = ({ checked, onChange }) => (
  <Box
    component="label"
    sx={{ display: 'inline-flex', alignItems: 'center', cursor: 'pointer', userSelect: 'none', flexShrink: 0 }}
  >
    <Box
      component="input"
      type="checkbox"
      checked={checked}
      onChange={(e: React.ChangeEvent<HTMLInputElement>) => onChange(e.target.checked)}
      sx={{ position: 'absolute', opacity: 0, width: 0, height: 0 }}
    />
    <Box sx={{
      width: 28, height: 16, borderRadius: 8, position: 'relative', transition: 'background .18s',
      background: checked ? 'var(--accent)' : 'rgba(128,128,128,.35)',
      border: '1px solid',
      borderColor: checked ? 'var(--accent)' : 'rgba(128,128,128,.4)',
    }}>
      <Box sx={{
        position: 'absolute', top: 2, left: checked ? 13 : 2,
        width: 10, height: 10, borderRadius: '50%',
        background: checked ? '#fff' : 'rgba(255,255,255,.5)',
        transition: 'left .18s',
      }} />
    </Box>
  </Box>
);

// ── Status chip (inline) ────────────────────────────────────────────────────

const InlineChip: React.FC<{ status: string }> = ({ status }) => {
  const c = availChip(status);
  return (
    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: '3px', fontSize: 9, fontWeight: 600, px: '6px', py: '1px', borderRadius: 10, color: c.color, background: c.bg, border: `1px solid ${c.dot}33`, whiteSpace: 'nowrap' }}>
      <Box sx={{ width: 5, height: 5, borderRadius: '50%', background: c.dot, flexShrink: 0 }} />
      {c.label}
    </Box>
  );
};

// ── Availability panel ─────────────────────────────────────────────────────

const DAYS = ['M', 'T', 'W', 'T', 'F', 'S', 'S'];

const fmtDateInput = (iso: string | null | undefined): string => {
  if (!iso) return '';
  return iso.slice(0, 10);
};

const fmtTimeInput = (iso: string | null | undefined): string => {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  return d.toTimeString().slice(0, 8);
};

const fmtDateDisplay = (iso: string | null | undefined): string => {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
};

const buildIso = (dateVal: string, timeVal: string): string | null => {
  if (!dateVal) return null;
  return `${dateVal}T${timeVal || '00:00:00'}`;
};

const AvailabilityPanel: React.FC<{
  file: MediaFile;
  onFileUpdated: (id: number, patch: Partial<MediaFile>) => void;
  onDelete: () => void;
}> = ({ file, onFileUpdated, onDelete }) => {
  const [periodEnabled, setPeriodEnabled] = useState(file.avail_period_enabled ?? true);
  const [fromDate, setFromDate] = useState(fmtDateInput(file.avail_from));
  const [fromTime, setFromTime] = useState(fmtDateInput(file.avail_from) ? (fmtTimeInput(file.avail_from) || '09:00:00') : '');
  const [toDate, setToDate] = useState(fmtDateInput(file.avail_to));
  const [toTime, setToTime] = useState(fmtDateInput(file.avail_to) ? (fmtTimeInput(file.avail_to) || '23:59:00') : '');

  const [schedEnabled, setSchedEnabled] = useState(file.avail_sched_enabled ?? true);
  const [schedRows, setSchedRows] = useState<AvailSchedRow[]>([]);
  const [saveError, setSaveError] = useState<string | null>(null);
  const fromDateRef = useRef<HTMLInputElement>(null);
  const toDateRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    axios.get(`/media/files/${file.id}/avail-schedule`)
      .then(r => setSchedRows(r.data))
      .catch(() => {});
  }, [file.id]);

  const patchPeriod = async (patch: Partial<MediaFile>, newStatus?: string) => {
    try {
      await axios.patch(`/media/files/${file.id}`, patch);
      onFileUpdated(file.id, {
        ...patch,
        ...(newStatus !== undefined ? { avail_status: newStatus as MediaFile['avail_status'] } : {}),
      });
    } catch { /* interceptor */ }
  };

  const computedStatus = (): string => {
    if (!periodEnabled) return 'active';
    const now = new Date();
    if (fromDate && new Date(`${fromDate}T${fromTime || '00:00:00'}`) > now) return 'not_started';
    if (toDate && new Date(`${toDate}T${toTime || '23:59:59'}`) < now) return 'expired';
    return 'active';
  };

  const toMin = (t: string) => { const [h, m] = t.split(':').map(Number); return h * 60 + m; };

  const validateSchedule = (): string | null => {
    for (let i = 0; i < schedRows.length; i++) {
      for (let j = i + 1; j < schedRows.length; j++) {
        const a = schedRows[i], b = schedRows[j];
        if ((a.days_mask & b.days_mask) === 0) continue;
        if (toMin(a.from_time) < toMin(b.to_time) && toMin(b.from_time) < toMin(a.to_time)) {
          return `Ranges ${a.from_time.slice(0,5)}–${a.to_time.slice(0,5)} and ${b.from_time.slice(0,5)}–${b.to_time.slice(0,5)} overlap`;
        }
      }
    }
    return null;
  };

  const handleSave = async () => {
    setSaveError(null);
    if (schedEnabled) {
      const err = validateSchedule();
      if (err) { setSaveError(err); return; }
    }
    const fromIso = fromDate ? buildIso(fromDate, fromTime || '09:00:00') : null;
    const toIso = toDate ? buildIso(toDate, toTime || '23:59:00') : null;

    // Auto-disable period if toggled on but no dates set
    let effectivePeriodEnabled = periodEnabled;
    if (periodEnabled && !fromIso && !toIso) {
      effectivePeriodEnabled = false;
      setPeriodEnabled(false);
      await patchPeriod({ avail_period_enabled: false } as any, 'active');
    }

    // Auto-disable schedule if toggled on but no rows added
    let effectiveSchedEnabled = schedEnabled;
    if (schedEnabled && schedRows.length === 0) {
      effectiveSchedEnabled = false;
      setSchedEnabled(false);
      try { await axios.patch(`/media/files/${file.id}`, { avail_sched_enabled: false }); } catch { /* interceptor */ }
    }

    const now = new Date();
    let newStatus = 'active';
    if (effectivePeriodEnabled) {
      if (fromIso && new Date(fromIso) > now) newStatus = 'not_started';
      else if (toIso && new Date(toIso) < now) newStatus = 'expired';
    }
    await patchPeriod({ avail_from: fromIso as any, avail_to: toIso as any }, newStatus);
    if (effectiveSchedEnabled || schedRows.length > 0) {
      try { await axios.put(`/media/files/${file.id}/avail-schedule`, { rows: schedRows }); } catch { /* interceptor */ }
    }
  };

  const status = computedStatus();
  const subHeaderSx = { display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0 };
  const subTitleSx = { fontSize: 10, fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'uppercase' as const, letterSpacing: '.07em' };
  const periodRowSx = { display: 'flex', alignItems: 'center', mb: '7px' };
  const labelSx = { fontSize: 10, color: 'var(--text-disabled)', width: 36, flexShrink: 0 };
  const dateValSx = { fontSize: 12, color: 'var(--text-primary)', fontVariantNumeric: 'tabular-nums' };
  const calBtnSx = { background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-disabled)', display: 'flex', alignItems: 'center', px: '2px', '&:hover': { color: 'var(--accent)' } };
  const clearBtnSx = { background: 'none', border: '1px solid transparent', borderRadius: '4px', cursor: 'pointer', color: 'var(--text-disabled)', fontSize: 9, width: 16, height: 16, display: 'flex', alignItems: 'center', justifyContent: 'center', ml: '2px', '&:hover': { background: 'rgba(248,113,113,.1)', color: '#f87171', borderColor: 'rgba(248,113,113,.2)' } };
  const timeInputSx = { background: 'transparent', border: 'none', borderBottom: '1px solid var(--glass-border)', color: 'var(--text-primary)', fontSize: 12, fontVariantNumeric: 'tabular-nums', width: 70, outline: 'none', textAlign: 'center' as const, '&::-webkit-calendar-picker-indicator': { display: 'none' }, '&::-webkit-inner-spin-button': { display: 'none' } };
  const dayBtnSx = (on: boolean) => ({ width: 20, height: 20, borderRadius: '50%', fontSize: 9, fontWeight: 700, cursor: 'pointer', border: '1px solid', background: on ? 'var(--accent)' : 'transparent', borderColor: on ? 'var(--accent)' : 'var(--glass-border)', color: on ? '#fff' : 'var(--text-disabled)', display: 'flex', alignItems: 'center', justifyContent: 'center', '&:hover': { borderColor: 'var(--accent)', color: on ? '#fff' : 'var(--accent)' } });
  const calIcon = <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>;

  return (
    <>
      {/* Availability period block */}
      <Box sx={{ borderRadius: '9px', p: '10px 12px', ...glassSx, mb: '8px' }}>
        <Box sx={subHeaderSx}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
            <Typography sx={subTitleSx}>Availability</Typography>
            <InlineChip status={status} />
          </Box>
          <PillToggle checked={periodEnabled} onChange={v => {
            setPeriodEnabled(v);
            const now = new Date();
            let newStatus = 'active';
            if (v && fromDate && new Date(`${fromDate}T${fromTime || '00:00:00'}`) > now) newStatus = 'not_started';
            else if (v && toDate && new Date(`${toDate}T${toTime || '23:59:59'}`) < now) newStatus = 'expired';
            patchPeriod({ avail_period_enabled: v } as any, newStatus);
          }} />
        </Box>
        {periodEnabled && (
          <Box sx={{ mt: '10px' }}>
            {/* From row */}
            <Box sx={periodRowSx}>
              <Typography sx={labelSx}>From</Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', flex: 1, justifyContent: 'space-between' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <Box component="button" onClick={() => fromDateRef.current?.showPicker?.()} sx={calBtnSx}>{calIcon}</Box>
                  <Box component="input" type="date" ref={fromDateRef} value={fromDate}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                      const d = e.target.value;
                      if (d && toDate && d > toDate) { setFromDate(''); setFromTime(''); return; }
                      setFromDate(d);
                      if (d && !fromTime) setFromTime('09:00:00');
                      if (!d) setFromTime('');
                    }}
                    sx={{ position: 'absolute', opacity: 0, pointerEvents: 'none', width: 0, height: 0 }}
                  />
                  <Typography sx={dateValSx}>{fromDate ? fmtDateDisplay(fromDate) : '—'}</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                  {fromDate && (
                    <Box component="input" type="time" step="1" value={fromTime}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFromTime(e.target.value)}
                      sx={timeInputSx}
                    />
                  )}
                  <Box component="button" onClick={() => { setFromDate(''); setFromTime(''); }} sx={clearBtnSx} title="Clear">✕</Box>
                </Box>
              </Box>
            </Box>
            {/* Until row */}
            <Box sx={{ ...periodRowSx, mb: 0 }}>
              <Typography sx={labelSx}>Until</Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', flex: 1, justifyContent: 'space-between' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <Box component="button" onClick={() => toDateRef.current?.showPicker?.()} sx={calBtnSx}>{calIcon}</Box>
                  <Box component="input" type="date" ref={toDateRef} value={toDate}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                      const d = e.target.value;
                      if (d && fromDate && d < fromDate) { setToDate(''); setToTime(''); return; }
                      setToDate(d);
                      if (d && !toTime) setToTime('23:59:00');
                      if (!d) setToTime('');
                    }}
                    sx={{ position: 'absolute', opacity: 0, pointerEvents: 'none', width: 0, height: 0 }}
                  />
                  <Typography sx={dateValSx}>{toDate ? fmtDateDisplay(toDate) : '—'}</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                  {toDate && (
                    <Box component="input" type="time" step="1" value={toTime}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => setToTime(e.target.value)}
                      sx={timeInputSx}
                    />
                  )}
                  <Box component="button" onClick={() => { setToDate(''); setToTime(''); }} sx={clearBtnSx} title="Clear">✕</Box>
                </Box>
              </Box>
            </Box>
          </Box>
        )}
      </Box>

      {/* Scheduled block */}
      <Box sx={{ borderRadius: '9px', p: '10px 12px', ...glassSx, mb: '8px' }}>
        <Box sx={subHeaderSx}>
          <Typography sx={subTitleSx}>Schedule</Typography>
          <PillToggle checked={schedEnabled} onChange={v => {
            setSchedEnabled(v);
            patchPeriod({ avail_sched_enabled: v } as any);
          }} />
        </Box>
        {schedEnabled && (
          <Box sx={{ mt: '10px' }}>
            {schedRows.map((row, idx) => (
              <Box key={idx} sx={{ mb: '8px', p: '8px', borderRadius: '7px', background: 'rgba(255,255,255,.03)', border: '1px solid var(--glass-border)' }}>
                <Box sx={{ display: 'flex', gap: '4px', mb: '6px' }}>
                  {DAYS.map((d, i) => {
                    const bit = 1 << i;
                    return (
                      <Box key={i} component="button" onClick={() => setSchedRows(prev => prev.map((r, ri) => ri === idx ? { ...r, days_mask: r.days_mask ^ bit } : r))} sx={dayBtnSx(!!(row.days_mask & bit))}>
                        {d}
                      </Box>
                    );
                  })}
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <Box component="input" type="time" step="1" value={row.from_time}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSchedRows(prev => prev.map((r, i) => i === idx ? { ...r, from_time: e.target.value || '00:00:00' } : r))}
                    sx={timeInputSx} />
                  <Typography sx={{ fontSize: 11, color: 'var(--text-disabled)' }}>—</Typography>
                  <Box component="input" type="time" step="1" value={row.to_time}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSchedRows(prev => prev.map((r, i) => i === idx ? { ...r, to_time: e.target.value || '00:00:00' } : r))}
                    sx={timeInputSx} />
                  <Box component="button" onClick={() => setSchedRows(prev => prev.filter((_, i) => i !== idx))} sx={{ ...clearBtnSx, ml: 'auto' }}>✕</Box>
                </Box>
              </Box>
            ))}
            <Box component="button" onClick={() => setSchedRows(prev => [...prev, { days_mask: 31, from_time: '09:00:00', to_time: '18:00:00' }])}
              sx={{ display: 'flex', alignItems: 'center', gap: '4px', background: 'none', border: '1px dashed var(--glass-border)', borderRadius: '7px', width: '100%', py: '5px', justifyContent: 'center', cursor: 'pointer', color: 'var(--text-disabled)', fontSize: 11, '&:hover': { borderColor: 'var(--accent)', color: 'var(--accent)' } }}
            >
              <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
              Add time range
            </Box>
          </Box>
        )}
      </Box>

      {saveError && (
        <Typography sx={{ fontSize: 11, color: '#f87171', mb: '6px', px: '2px' }}>{saveError}</Typography>
      )}

      {/* Delete + Save */}
      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px' }}>
        <Box component="button" onClick={onDelete} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px', ...glassSx, color: '#f87171', borderRadius: '7px', py: '7px', fontSize: 11.5, cursor: 'pointer', '&:hover': { bgcolor: 'rgba(248,113,113,.08)', color: '#fca5a5' } }}>
          <IcoTrash /> Delete
        </Box>
        <Box component="button" onClick={handleSave} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', ...glassSx, bgcolor: 'var(--accent)', borderColor: 'var(--accent)', color: '#fff', borderRadius: '7px', py: '7px', fontSize: 11.5, fontWeight: 600, cursor: 'pointer', '&:hover': { opacity: .88 } }}>
          Save
        </Box>
      </Box>
    </>
  );
};

// ── Detail panel ───────────────────────────────────────────────────────────

const DetailPanel: React.FC<{
  file: MediaFile | null;
  t: (k: string) => string;
  onDelete: (id: number) => void;
  onEdit: (id: number) => void;
  onFileUpdated: (id: number, patch: Partial<MediaFile>) => void;
}> = ({ file, t, onDelete, onEdit, onFileUpdated }) => {
  const navigate = useNavigate();

  // Local editable state — sync when file changes
  const [defDurSec, setDefDurSec] = useState<string>('15');
  const [roundDur, setRoundDur] = useState<boolean>(false);
  const [nameEditing, setNameEditing] = useState(false);
  const [nameValue, setNameValue] = useState('');

  // Used-in section
  interface FileUsages {
    playlists: { playlistId: number; playlistName: string; position: number }[];
    schedules: { scheduleId: number; scheduleName: string; eventName: string }[];
    screens: { screenId: number; deviceId: string; displayName: string | null }[];
  }
  const [usages, setUsages] = useState<FileUsages | null>(null);
  const [usedInExpanded, setUsedInExpanded] = useState(false);

  useEffect(() => {
    if (!file) return;
    setDefDurSec(file.default_duration_ms != null ? String(Math.round(file.default_duration_ms / 1000)) : '15');
    const isVid = file.mime_type?.startsWith('video/');
    setRoundDur(isVid ? (file.round_duration ?? true) : false);
    setNameEditing(false);
    setUsages(null);
    setUsedInExpanded(false);
    axios.get<FileUsages>(`/media/files/${file.id}/usages`)
      .then(r => setUsages(r.data))
      .catch(() => {});
  }, [file?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const commitName = async () => {
    if (!file) return;
    const trimmed = nameValue.trim();
    setNameEditing(false);
    // Редактируется только имя; расширение приклеиваем обратно, чтобы не потерять тип файла
    const full = trimmed + nameExt(file.original_name);
    if (!trimmed || full === file.original_name) return;
    try {
      await axios.patch(`/media/files/${file.id}`, { name: full });
      onFileUpdated(file.id, { original_name: full });
    } catch { /* interceptor */ }
  };

  const saveField = async (patch: { default_duration_ms?: number | null; round_duration?: boolean; default_scale_mode?: string }) => {
    if (!file) return;
    try {
      await axios.patch(`/media/files/${file.id}`, patch);
      onFileUpdated(file.id, patch as Partial<MediaFile>);
    } catch { /* interceptor */ }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>

      <Box sx={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
        {!file ? (
          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 1.5, px: 3 }}>
            <Box sx={{ width: 44, height: 44, borderRadius: '12px', bgcolor: 'var(--selected-bg)', border: '1px solid var(--glass-border)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--accent)' }}>
              <IcoImage size={20} />
            </Box>
            <Typography sx={{ fontSize: 13, color: 'var(--text-disabled)', textAlign: 'center' }}>{t('media.selectFile')}</Typography>
          </Box>
        ) : (
          <Box sx={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column' }}>
            {(() => {
              const iv = isVideo(file.mime_type);
              const ext = file.original_name.split('.').pop()?.toUpperCase() ?? '';
              const hasFracMs = iv && file.duration_ms != null && (file.duration_ms % 1000) !== 0;
              const codecStr = file.video_codec
                ? `${file.video_codec}${file.audio_codec ? ` / ${file.audio_codec}` : ''}`
                : null;
              const metaRows: [string, string | React.ReactNode][] = [
                [t('media.resolution'), file.width && file.height ? `${file.width}×${file.height}` : '—'],
                [t('media.size'), fmtSizeMB(file.size_bytes)],
                ...(iv && file.duration_ms ? [[t('media.bitrate'), fmtBitrate(file.size_bytes, file.duration_ms) ?? '—'] as [string, string]] : []),
                ...(iv && codecStr ? [[t('media.codec'), codecStr] as [string, string]] : []),
                ...(iv && file.bit_depth ? [[t('media.colorDepth'), (
                  <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: '5px' }}>
                    <span>{file.bit_depth}-bit</span>
                    {file.bit_depth > 8 && (
                      <Box component="span" title={t('media.colorDepthWarn')} sx={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 16, height: 16, borderRadius: '50%', background: 'rgba(255,170,0,0.15)', color: '#ffaa00', fontSize: 11, fontWeight: 700, cursor: 'default', flexShrink: 0 }}>!</Box>
                    )}
                  </Box>
                )] as [string, React.ReactNode]] : []),
              ];
              return (
                <>
                  <Box sx={{ px: '12px', pt: '12px', flexShrink: 0 }}>
                    <FilePreview key={file.id} file={file} onEdit={() => onEdit(file.id)} />
                  </Box>
                  <Box sx={{ p: '12px 14px' }}>
                    {nameEditing ? (
                      <Box
                        component="input"
                        autoFocus
                        value={nameValue}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNameValue(e.target.value)}
                        onBlur={commitName}
                        onKeyDown={(e: React.KeyboardEvent) => {
                          if (e.key === 'Enter') commitName();
                          if (e.key === 'Escape') setNameEditing(false);
                        }}
                        sx={{
                          display: 'block', width: '100%', boxSizing: 'border-box',
                          fontSize: 13, fontWeight: 600, color: 'var(--text-primary)',
                          background: 'var(--input-bg)', border: '1px solid var(--accent)',
                          borderRadius: '6px', px: '8px', py: '4px', mb: '2px',
                          outline: 'none', fontFamily: 'inherit',
                        }}
                      />
                    ) : (
                      <Typography
                        onDoubleClick={() => { setNameValue(baseName(file.original_name)); setNameEditing(true); }}
                        title="Double-click to rename"
                        sx={{ fontSize: 13, fontWeight: 600, wordBreak: 'break-all', mb: '2px', color: 'var(--text-primary)', cursor: 'text', userSelect: 'none' }}
                      >
                        {baseName(file.original_name)}
                      </Typography>
                    )}
                    <Typography sx={{ fontSize: 11, color: 'var(--text-disabled)', mb: '12px' }}>{iv ? 'Video' : 'Image'} · {ext} · {new Date(file.created_at).toLocaleDateString()}</Typography>

                    {/* Duration / default duration block */}
                    {iv ? (
                      /* VIDEO — duration with ms + round checkbox */
                      <Box sx={{ borderRadius: '9px', p: '10px 12px', ...glassSx, mb: '10px' }}>
                        <Typography sx={{ fontSize: 10, color: 'var(--text-disabled)', textTransform: 'uppercase', letterSpacing: '.08em', mb: '8px', fontWeight: 600 }}>{t('media.duration')}</Typography>
                        {(() => {
                          const ms = file.duration_ms ?? 0;
                          const millis = ms % 1000;
                          const totalSec = Math.floor(ms / 1000);
                          const min = Math.floor(totalSec / 60);
                          const sec = totalSec % 60;
                          return (
                            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: '10px' }}>
                              {[
                                { val: String(min).padStart(2, '0'), label: 'min' },
                                { val: String(sec).padStart(2, '0'), label: 'sec' },
                                { val: String(millis).padStart(3, '0'), label: 'ms' },
                              ].map(({ val, label }, i) => (
                                <Box key={i} sx={{ display: 'flex', alignItems: 'baseline', gap: '3px' }}>
                                  <Typography sx={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)', fontVariantNumeric: 'tabular-nums', lineHeight: 1 }}>
                                    {val}
                                  </Typography>
                                  <Typography sx={{ fontSize: 10, color: 'var(--text-disabled)', textTransform: 'uppercase', letterSpacing: '.04em' }}>
                                    {label}
                                  </Typography>
                                </Box>
                              ))}
                            </Box>
                          );
                        })()}
                        {/* Round checkbox */}
                        <Box
                          component="label"
                          sx={{ display: 'flex', alignItems: 'center', gap: '8px', mt: '10px', cursor: 'pointer', userSelect: 'none' }}
                        >
                          <Box
                            component="input"
                            type="checkbox"
                            checked={roundDur}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                              const v = e.target.checked;
                              setRoundDur(v);
                              saveField({ round_duration: v });
                            }}
                            sx={{ width: 14, height: 14, accentColor: 'var(--accent)', cursor: 'pointer' }}
                          />
                          <Typography sx={{ fontSize: 12, color: 'var(--text-secondary)' }}>
                            Round to whole second
                          </Typography>
                        </Box>
                      </Box>
                    ) : (
                      /* IMAGE — default duration field */
                      <Box sx={{ borderRadius: '9px', p: '10px 12px', ...glassSx, mb: '10px' }}>
                        <Typography sx={{ fontSize: 10, color: 'var(--text-disabled)', textTransform: 'uppercase', letterSpacing: '.08em', mb: '6px', fontWeight: 600 }}>Default duration (sec)</Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <Box
                            component="input"
                            type="number"
                            min={1}
                            max={3600}
                            value={defDurSec}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setDefDurSec(e.target.value)}
                            onBlur={() => {
                              const sec = parseInt(defDurSec, 10);
                              if (!isNaN(sec) && sec > 0) {
                                saveField({ default_duration_ms: sec * 1000 });
                              } else {
                                setDefDurSec('15');
                              }
                            }}
                            onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
                              if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
                            }}
                            sx={{ width: 70, background: 'transparent', border: 'none', borderBottom: '1.5px solid var(--accent)', color: 'var(--text-primary)', fontSize: 15, fontWeight: 700, outline: 'none', pb: '2px', textAlign: 'center', fontVariantNumeric: 'tabular-nums' }}
                          />
                          <Typography sx={{ fontSize: 12, color: 'var(--text-disabled)' }}>seconds</Typography>
                        </Box>
                      </Box>
                    )}

                    {/* Scale mode block */}
                    <Box sx={{ borderRadius: '9px', p: '10px 12px', ...glassSx, mb: '10px' }}>
                      <Typography sx={{ fontSize: 10, color: 'var(--text-disabled)', textTransform: 'uppercase', letterSpacing: '.08em', mb: '6px', fontWeight: 600 }}>Default scale</Typography>
                      <Box sx={{ display: 'flex', gap: '4px' }}>
                        {(['fill', 'fit', 'stretch'] as const).map(mode => (
                          <Box
                            key={mode}
                            component="button"
                            onClick={() => saveField({ default_scale_mode: mode })}
                            sx={{ flex: 1, py: '5px', fontSize: 11, fontWeight: 600, fontFamily: 'inherit', border: 'none', borderRadius: '6px', cursor: 'pointer', transition: 'all .12s', background: (file.default_scale_mode ?? 'fill') === mode ? 'rgba(99,102,241,.3)' : 'var(--glass-hover)', color: (file.default_scale_mode ?? 'fill') === mode ? 'var(--accent-light)' : 'var(--text-secondary)', '&:hover': { background: 'var(--glass-hover)', color: 'var(--text-primary)' } }}
                          >
                            {mode.charAt(0).toUpperCase() + mode.slice(1)}
                          </Box>
                        ))}
                      </Box>
                    </Box>

                    {/* Metadata block */}
                    <Box sx={{ borderRadius: '9px', p: '8px 12px', ...glassSx, mb: '12px', display: 'flex', flexDirection: 'column', gap: '5px' }}>
                      {metaRows.map(([label, val]) => (
                        <Box key={label} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: '8px' }}>
                          <Typography sx={{ fontSize: 11, color: 'var(--text-disabled)', flexShrink: 0 }}>{label}</Typography>
                          <Typography sx={{ fontSize: 12, color: 'var(--text-primary)', fontVariantNumeric: 'tabular-nums', textAlign: 'right' }}>{val}</Typography>
                        </Box>
                      ))}
                    </Box>

                    {/* 10-bit convert banner */}
                    {iv && file.bit_depth && file.bit_depth > 8 && !file.transcoding && (
                      <Box sx={{ borderRadius: '9px', p: '10px 12px', mb: '10px', background: 'rgba(255,170,0,0.08)', border: '1px solid rgba(255,170,0,0.25)', display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <Box sx={{ fontSize: 16, lineHeight: 1, flexShrink: 0 }}>⚠️</Box>
                        <Box sx={{ flex: 1 }}>
                          <Typography sx={{ fontSize: 12, fontWeight: 600, color: '#ffaa00', mb: '2px' }}>{file.bit_depth}-bit — not supported on all devices</Typography>
                          <Typography sx={{ fontSize: 11, color: 'var(--text-secondary)' }}>Convert to 8-bit H.264 for maximum compatibility</Typography>
                        </Box>
                        <Box
                          component="button"
                          onClick={async () => {
                            try {
                              await axios.post(`/media/files/${file.id}/convert-to-8bit`);
                              onFileUpdated(file.id, { transcoding: true });
                            } catch {}
                          }}
                          sx={{ flexShrink: 0, background: 'rgba(255,170,0,0.15)', border: '1px solid rgba(255,170,0,0.4)', borderRadius: '7px', color: '#ffaa00', fontSize: 12, fontWeight: 600, px: '10px', py: '5px', cursor: 'pointer', '&:hover': { background: 'rgba(255,170,0,0.25)' } }}
                        >
                          Convert
                        </Box>
                      </Box>
                    )}
                    {iv && file.transcoding && (
                      <Box sx={{ borderRadius: '9px', p: '10px 12px', mb: '10px', background: 'rgba(99,102,241,0.08)', border: '1px solid rgba(99,102,241,0.25)', display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <Box sx={{ fontSize: 16, lineHeight: 1, flexShrink: 0 }}>⏳</Box>
                        <Typography sx={{ fontSize: 12, color: 'var(--text-secondary)' }}>Converting to 8-bit…</Typography>
                      </Box>
                    )}

                    {/* Used-in block */}
                    {usages !== null && (() => {
                      const playlistGroups = usages.playlists.reduce((acc, p) => {
                        if (!acc[p.playlistId]) acc[p.playlistId] = { id: p.playlistId, name: p.playlistName, count: 0 };
                        acc[p.playlistId].count++;
                        return acc;
                      }, {} as Record<number, { id: number; name: string; count: number }>);
                      const plGroups = Object.values(playlistGroups);
                      const totalPlaces = plGroups.length + usages.schedules.length;
                      return (
                        <Box sx={{ borderRadius: '9px', ...glassSx, mb: '10px', overflow: 'hidden' }}>
                          {/* Header — always visible */}
                          <Box
                            onClick={() => setUsedInExpanded(v => !v)}
                            sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: '12px', py: '8px', cursor: 'pointer', userSelect: 'none', '&:hover': { bgcolor: 'var(--glass-hover)' } }}
                          >
                            <Typography sx={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.08em', color: 'var(--text-disabled)' }}>
                              Used in
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                              <Box sx={{ fontSize: 11, fontWeight: 700, px: '7px', py: '1px', borderRadius: 10, bgcolor: totalPlaces > 0 ? 'rgba(99,102,241,.15)' : 'rgba(128,128,128,.1)', color: totalPlaces > 0 ? 'var(--accent-light)' : 'var(--text-disabled)' }}>
                                {totalPlaces} {totalPlaces === 1 ? 'place' : 'places'}
                              </Box>
                              <Box sx={{ color: 'var(--text-disabled)', fontSize: 10, transition: 'transform .15s', transform: usedInExpanded ? 'rotate(180deg)' : 'rotate(0deg)' }}>▾</Box>
                            </Box>
                          </Box>
                          {/* Expanded content */}
                          {usedInExpanded && (
                            <Box sx={{ borderTop: '1px solid var(--divider)', px: '12px', pt: '8px', pb: '10px', display: 'flex', flexDirection: 'column', gap: '4px' }}>
                              {totalPlaces === 0 ? (
                                <Typography sx={{ fontSize: 11, color: 'var(--text-disabled)', py: '2px' }}>Not used anywhere</Typography>
                              ) : (
                                <>
                                  {plGroups.length > 0 && (
                                    <>
                                      <Typography sx={{ fontSize: 10, fontWeight: 600, color: 'var(--text-disabled)', textTransform: 'uppercase', letterSpacing: '.06em', mb: '2px', mt: '2px' }}>Playlists</Typography>
                                      {plGroups.map(pl => (
                                        <Box key={pl.id} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px', py: '2px' }}>
                                          <Box
                                            component="span"
                                            onClick={() => navigate(`/playlists/${pl.id}`)}
                                            sx={{ fontSize: 12, color: 'var(--accent-light)', cursor: 'pointer', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1, '&:hover': { textDecoration: 'underline' } }}
                                          >
                                            {pl.name}
                                          </Box>
                                          {pl.count > 1 && (
                                            <Box sx={{ fontSize: 10, fontWeight: 600, px: '6px', py: '1px', borderRadius: 10, bgcolor: 'rgba(99,102,241,.1)', color: 'var(--accent-light)', flexShrink: 0 }}>×{pl.count}</Box>
                                          )}
                                        </Box>
                                      ))}
                                    </>
                                  )}
                                  {usages.schedules.length > 0 && (
                                    <>
                                      <Typography sx={{ fontSize: 10, fontWeight: 600, color: 'var(--text-disabled)', textTransform: 'uppercase', letterSpacing: '.06em', mb: '2px', mt: plGroups.length > 0 ? '6px' : '2px' }}>Schedules</Typography>
                                      {usages.schedules.map((s, i) => (
                                        <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: '6px', py: '2px' }}>
                                          <Box
                                            component="span"
                                            onClick={() => navigate(`/schedules/${s.scheduleId}`)}
                                            sx={{ fontSize: 12, color: 'var(--accent-light)', cursor: 'pointer', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', '&:hover': { textDecoration: 'underline' } }}
                                          >
                                            {s.scheduleName}
                                          </Box>
                                          <Typography sx={{ fontSize: 11, color: 'var(--text-disabled)', flexShrink: 0 }}>/ {s.eventName}</Typography>
                                        </Box>
                                      ))}
                                    </>
                                  )}
                                </>
                              )}
                            </Box>
                          )}
                        </Box>
                      );
                    })()}

                    <AvailabilityPanel key={file.id} file={file} onFileUpdated={onFileUpdated} onDelete={() => onDelete(file.id)} />
                  </Box>
                </>
              );
            })()}
          </Box>
        )}
      </Box>
    </Box>
  );
};


// ── Folder action menu ─────────────────────────────────────────────────────

const FolderActionMenu: React.FC<{ folderId: number; folderName: string; existingNames: string[]; onDelete: (id: number) => void; onRename: (id: number, name: string) => void }> = ({ folderId, folderName, existingNames, onDelete, onRename }) => {
  const [open, setOpen] = useState(false);
  const [renaming, setRenaming] = useState(false);
  const [name, setName] = useState(folderName);

  const startRename = (e: React.MouseEvent) => {
    e.stopPropagation();
    setName(folderName);
    setRenaming(true);
    setOpen(false);
  };

  const isDuplicate = name.trim() !== '' && name.trim().toLowerCase() !== folderName.toLowerCase() && existingNames.some(n => n.toLowerCase() === name.trim().toLowerCase());
  const canSave = name.trim() !== '' && !isDuplicate;

  if (renaming) {
    return (
      <Box onClick={e => e.stopPropagation()} sx={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
          <Box
            component="input"
            autoFocus
            value={name}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setName(e.target.value)}
            onKeyDown={(e: React.KeyboardEvent) => {
              if (e.key === 'Enter' && canSave) { onRename(folderId, name.trim()); setRenaming(false); }
              if (e.key === 'Escape') setRenaming(false);
            }}
            onFocus={(e: React.FocusEvent<HTMLInputElement>) => e.target.select()}
            sx={{ background: 'var(--glass-bg)', border: `1px solid ${isDuplicate ? '#f87171' : 'var(--accent)'}`, borderRadius: '6px', px: '8px', py: '3px', color: 'var(--text-primary)', fontSize: 13, outline: 'none', width: 140 }}
          />
          <Box component="button" onClick={() => { if (canSave) { onRename(folderId, name.trim()); setRenaming(false); } }} disabled={!canSave} sx={{ px: '7px', py: '3px', borderRadius: '6px', bgcolor: canSave ? 'var(--accent)' : 'var(--glass-hover)', border: '1px solid var(--accent)', color: '#fff', fontSize: 12, cursor: canSave ? 'pointer' : 'default', opacity: canSave ? 1 : 0.4 }}>
            Save
          </Box>
        </Box>
        {isDuplicate && <Typography sx={{ fontSize: 11, color: '#f87171', ml: '2px' }}>Already exists</Typography>}
      </Box>
    );
  }

  return (
    <Box sx={{ position: 'relative' }}>
      <Box component="button" onClick={e => { e.stopPropagation(); setOpen(o => !o); }} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 30, height: 30, borderRadius: '7px', cursor: 'pointer', background: 'none', border: '1px solid transparent', color: 'var(--text-disabled)', '&:hover': { bgcolor: 'var(--glass-hover)', borderColor: 'var(--glass-border)', color: 'var(--text-primary)' } }}>
        <IcoDots />
      </Box>
      {open && (
        <>
          <Box onClick={e => { e.stopPropagation(); setOpen(false); }} sx={{ position: 'fixed', inset: 0, zIndex: 100 }} />
          <Box sx={{ position: 'absolute', right: 0, top: 34, zIndex: 200, width: 140, borderRadius: '10px', overflow: 'hidden', ...glassSx, boxShadow: '0 8px 32px rgba(0,0,0,.5)', py: '4px' }}>
            <Box component="button" onClick={startRename} sx={{ display: 'flex', alignItems: 'center', gap: '8px', width: '100%', px: '12px', py: '8px', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-secondary)', fontSize: 13, '&:hover': { bgcolor: 'var(--glass-hover)', color: 'var(--text-primary)' } }}>
              <IcoPencil /> Rename
            </Box>
            <Box component="button" onClick={e => { e.stopPropagation(); setOpen(false); onDelete(folderId); }} sx={{ display: 'flex', alignItems: 'center', gap: '8px', width: '100%', px: '12px', py: '8px', background: 'none', border: 'none', cursor: 'pointer', color: '#f87171', fontSize: 13, '&:hover': { bgcolor: 'rgba(248,113,113,.08)', color: '#fca5a5' } }}>
              <IcoTrash /> Delete
            </Box>
          </Box>
        </>
      )}
    </Box>
  );
};

// ── Action menu ────────────────────────────────────────────────────────────

const ActionMenu: React.FC<{ fileId: number; fileName: string; onDelete: (id: number) => void; onDownload: (id: number, name: string) => void; onRename: (id: number, name: string) => void }> = ({ fileId, fileName, onDelete, onDownload, onRename }) => {
  const [open, setOpen] = useState(false);

  return (
    <Box sx={{ position: 'relative' }}>
      <Box component="button" onClick={e => { e.stopPropagation(); setOpen(o => !o); }} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 30, height: 30, borderRadius: '7px', cursor: 'pointer', background: 'none', border: '1px solid transparent', color: 'var(--text-disabled)', '&:hover': { bgcolor: 'var(--glass-hover)', borderColor: 'var(--glass-border)', color: 'var(--text-primary)' } }}>
        <IcoDots />
      </Box>
      {open && (
        <>
          <Box onClick={e => { e.stopPropagation(); setOpen(false); }} sx={{ position: 'fixed', inset: 0, zIndex: 100 }} />
          <Box sx={{ position: 'absolute', right: 0, top: 34, zIndex: 200, width: 148, borderRadius: '10px', overflow: 'hidden', ...glassSx, boxShadow: '0 8px 32px rgba(0,0,0,.5)', py: '4px' }}>
            <Box component="button" onClick={e => { e.stopPropagation(); setOpen(false); onRename(fileId, fileName); }} sx={{ display: 'flex', alignItems: 'center', gap: '8px', width: '100%', px: '12px', py: '8px', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-secondary)', fontSize: 13, '&:hover': { bgcolor: 'var(--glass-hover)', color: 'var(--text-primary)' } }}>
              ✎ Rename
            </Box>
            <Box component="button" onClick={e => { e.stopPropagation(); setOpen(false); onDownload(fileId, fileName); }} sx={{ display: 'flex', alignItems: 'center', gap: '8px', width: '100%', px: '12px', py: '8px', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-secondary)', fontSize: 13, '&:hover': { bgcolor: 'var(--glass-hover)', color: 'var(--text-primary)' } }}>
              <IcoDownload /> Download
            </Box>
            <Box component="button" onClick={e => { e.stopPropagation(); setOpen(false); onDelete(fileId); }} sx={{ display: 'flex', alignItems: 'center', gap: '8px', width: '100%', px: '12px', py: '8px', background: 'none', border: 'none', cursor: 'pointer', color: '#f87171', fontSize: 13, '&:hover': { bgcolor: 'rgba(248,113,113,.08)', color: '#fca5a5' } }}>
              <IcoTrash /> Delete
            </Box>
          </Box>
        </>
      )}
    </Box>
  );
};

// ── ConflictModal ──────────────────────────────────────────────────────────

const API_BASE_THUMB = process.env.REACT_APP_API_URL || 'http://localhost:3002/api';

const ConflictModal: React.FC<{
  items: ConflictItem[];
  existingNames: string[];
  totalFiles: number;
  onResolve: (items: ConflictItem[]) => void;
  onCancel: () => void;
}> = ({ items: initial, existingNames, totalFiles, onResolve, onCancel }) => {
  const [items, setItems] = useState<ConflictItem[]>(initial);

  const set = (index: number, patch: Partial<ConflictItem>) =>
    setItems(prev => prev.map(it => it.index === index ? { ...it, ...patch } : it));

  const applyAll = (action: ConflictAction) =>
    setItems(prev => prev.map(it => {
      const base: ConflictItem = { ...it, action };
      if (action === 'copy') base.newName = suggestCopyName(it.localFile.name, existingNames);
      if (action === 'rename') base.newName = it.localFile.name;
      return base;
    }));

  const uploadCount = items.filter(it => it.action !== 'skip').length;
  const nonConflictCount = totalFiles - items.length;

  return (
    <Box sx={{ position: 'fixed', inset: 0, zIndex: 9000, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'rgba(0,0,0,.7)', backdropFilter: 'blur(8px)' }}
      onClick={e => { if (e.target === e.currentTarget) onCancel(); }}>
      <Box sx={{ width: '100%', maxWidth: 760, maxHeight: '90vh', display: 'flex', flexDirection: 'column', borderRadius: '16px', overflow: 'hidden', background: 'var(--glass-bg)', backdropFilter: 'blur(24px)', border: '1px solid var(--glass-border)', boxShadow: '0 32px 80px rgba(0,0,0,.5)' }}>

        {/* Header */}
        <Box sx={{ px: '24px', py: '18px', borderBottom: '1px solid var(--glass-border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
          <Box>
            <Typography sx={{ fontSize: 16, fontWeight: 700, color: 'var(--text-primary)' }}>Duplicate files found</Typography>
            <Typography sx={{ fontSize: 13, color: 'var(--text-disabled)', mt: '2px' }}>{items.length} file{items.length > 1 ? 's' : ''} already exist on the server</Typography>
          </Box>
          <Box component="button" onClick={onCancel} sx={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-disabled)', p: '4px', display: 'flex', '&:hover': { color: 'var(--text-primary)' } }}>
            <svg width={20} height={20} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </Box>
        </Box>

        {/* Apply-all bar */}
        <Box sx={{ px: '24px', py: '10px', borderBottom: '1px solid var(--glass-border)', display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0, bgcolor: 'var(--glass-hover)' }}>
          <Typography sx={{ fontSize: 12, color: 'var(--text-disabled)', mr: '4px' }}>Apply to all:</Typography>
          {(['skip', 'copy', 'rename', 'overwrite'] as ConflictAction[]).map(a => (
            <Box key={a} component="button" onClick={() => applyAll(a)}
              sx={{ px: '10px', py: '4px', borderRadius: '6px', border: '1px solid var(--glass-border)', background: 'var(--glass-hover)', cursor: 'pointer', color: 'var(--text-secondary)', fontSize: 12, '&:hover': { borderColor: 'var(--accent)', color: 'var(--accent-light)' } }}>
              {a === 'skip' ? 'Skip all' : a === 'copy' ? 'Create copies' : a === 'rename' ? 'Rename all' : 'Overwrite all'}
            </Box>
          ))}
        </Box>

        {/* Conflict list */}
        <Box sx={{ overflowY: 'auto', flex: 1, minHeight: 0, px: '24px', py: '16px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
          {items.map(item => {
            const ex = item.existing;
            const isDup = item.action === 'rename' && item.newName.trim() !== '' && item.newName.trim().toLowerCase() !== item.localFile.name.toLowerCase() && existingNames.some(n => n.toLowerCase() === item.newName.trim().toLowerCase());
            const localDate = new Date(item.localFile.lastModified).toLocaleDateString();
            const serverDate = new Date(ex.created_at).toLocaleDateString();
            const sizeDiff = item.localFile.size - ex.size_bytes;
            return (
              <Box key={item.index} sx={{ borderRadius: '12px', border: '1px solid var(--glass-border)', overflow: 'hidden', background: 'var(--glass-bg)' }}>
                {/* Side-by-side info */}
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr auto 1fr', gap: 0 }}>
                  {/* LEFT: new file */}
                  <Box sx={{ p: '14px 16px', display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
                    <Box sx={{ width: 48, height: 48, borderRadius: '8px', bgcolor: isVideo(item.localFile.type) ? 'rgba(99,102,241,.15)' : 'rgba(52,211,153,.12)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, border: '1px solid rgba(255,255,255,.08)' }}>
                      {isVideo(item.localFile.type)
                        ? <svg width={22} height={22} viewBox="0 0 24 24" fill="none" stroke="rgba(129,140,248,.9)" strokeWidth={1.5} strokeLinecap="round"><rect x="2" y="2" width="20" height="20" rx="3"/><polygon points="10 8 16 12 10 16 10 8" fill="rgba(129,140,248,.9)" stroke="none"/></svg>
                        : <svg width={22} height={22} viewBox="0 0 24 24" fill="none" stroke="rgba(52,211,153,.9)" strokeWidth={1.5} strokeLinecap="round"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5" fill="rgba(52,211,153,.9)" stroke="none"/><polyline points="21 15 16 10 5 21" stroke="rgba(52,211,153,.9)"/></svg>
                      }
                    </Box>
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Typography sx={{ fontSize: 11, fontWeight: 700, color: 'var(--accent-light)', mb: '4px', textTransform: 'uppercase', letterSpacing: '.06em' }}>New file</Typography>
                      <Typography sx={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)', wordBreak: 'break-all', lineHeight: 1.3 }}>{item.localFile.name}</Typography>
                      <Box sx={{ mt: '6px', display: 'flex', flexDirection: 'column', gap: '2px' }}>
                        <Typography sx={{ fontSize: 12, color: 'var(--text-secondary)' }}>Size: <b style={{ color: 'var(--text-primary)' }}>{fmtSizeMB(item.localFile.size)}</b>
                          {sizeDiff !== 0 && <span style={{ color: sizeDiff > 0 ? '#4ade80' : '#f87171', marginLeft: 4 }}>({sizeDiff > 0 ? '+' : ''}{fmtSizeMB(Math.abs(sizeDiff))})</span>}
                        </Typography>
                        <Typography sx={{ fontSize: 12, color: 'var(--text-secondary)' }}>Modified: <b style={{ color: 'var(--text-primary)' }}>{localDate}</b></Typography>
                        {item.localMeta.width && item.localMeta.height && <Typography sx={{ fontSize: 12, color: 'var(--text-secondary)' }}>Resolution: <b style={{ color: 'var(--text-primary)' }}>{item.localMeta.width}×{item.localMeta.height}</b></Typography>}
                        {item.localMeta.durationMs && fmtBitrate(item.localFile.size, item.localMeta.durationMs) && <Typography sx={{ fontSize: 12, color: 'var(--text-secondary)' }}>Bitrate: <b style={{ color: 'var(--text-primary)' }}>{fmtBitrate(item.localFile.size, item.localMeta.durationMs)}</b></Typography>}
                        {item.localMeta.videoCodec && <Typography sx={{ fontSize: 12, color: 'var(--text-secondary)' }}>Codec: <b style={{ color: 'var(--text-primary)' }}>{item.localMeta.videoCodec}{item.localMeta.audioCodec ? ` / ${item.localMeta.audioCodec}` : ''}</b></Typography>}
                        {item.localMeta.durationMs && <Typography sx={{ fontSize: 12, color: 'var(--text-secondary)' }}>Duration: <b style={{ color: 'var(--text-primary)' }}>{fmtDur(item.localMeta.durationMs)}</b></Typography>}
                      </Box>
                    </Box>
                  </Box>

                  {/* Divider */}
                  <Box sx={{ display: 'flex', alignItems: 'center', px: '8px', borderLeft: '1px solid var(--glass-border)', borderRight: '1px solid var(--glass-border)' }}>
                    <Typography sx={{ fontSize: 11, color: 'var(--text-disabled)', fontWeight: 700 }}>VS</Typography>
                  </Box>

                  {/* RIGHT: existing file */}
                  <Box sx={{ p: '14px 16px', display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
                    <Box sx={{ width: 48, height: 48, borderRadius: '8px', overflow: 'hidden', flexShrink: 0, bgcolor: 'rgba(255,255,255,.05)', border: '1px solid rgba(255,255,255,.08)' }}>
                      {isImage(ex.mime_type)
                        ? <img src={`${API_BASE_THUMB}/media/thumb/${ex.id}`} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }} />
                        : <Box sx={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'rgba(99,102,241,.12)' }}>
                            <svg width={22} height={22} viewBox="0 0 24 24" fill="none" stroke="rgba(129,140,248,.7)" strokeWidth={1.5} strokeLinecap="round"><rect x="2" y="2" width="20" height="20" rx="3"/><polygon points="10 8 16 12 10 16 10 8" fill="rgba(129,140,248,.7)" stroke="none"/></svg>
                          </Box>
                      }
                    </Box>
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#fbbf24', mb: '4px', textTransform: 'uppercase', letterSpacing: '.06em' }}>On server</Typography>
                      <Typography sx={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)', wordBreak: 'break-all', lineHeight: 1.3 }}>{ex.original_name}</Typography>
                      <Box sx={{ mt: '6px', display: 'flex', flexDirection: 'column', gap: '2px' }}>
                        <Typography sx={{ fontSize: 12, color: 'var(--text-secondary)' }}>Size: <b style={{ color: 'var(--text-primary)' }}>{fmtSizeMB(ex.size_bytes)}</b></Typography>
                        <Typography sx={{ fontSize: 12, color: 'var(--text-secondary)' }}>Uploaded: <b style={{ color: 'var(--text-primary)' }}>{serverDate}</b></Typography>
                        {ex.width && ex.height && <Typography sx={{ fontSize: 12, color: 'var(--text-secondary)' }}>Resolution: <b style={{ color: 'var(--text-primary)' }}>{ex.width}×{ex.height}</b></Typography>}
                        {ex.duration_ms && fmtBitrate(ex.size_bytes, ex.duration_ms) && <Typography sx={{ fontSize: 12, color: 'var(--text-secondary)' }}>Bitrate: <b style={{ color: 'var(--text-primary)' }}>{fmtBitrate(ex.size_bytes, ex.duration_ms)}</b></Typography>}
                        {ex.video_codec && <Typography sx={{ fontSize: 12, color: 'var(--text-secondary)' }}>Codec: <b style={{ color: 'var(--text-primary)' }}>{ex.video_codec}{ex.audio_codec ? ` / ${ex.audio_codec}` : ''}</b></Typography>}
                        {ex.duration_ms && <Typography sx={{ fontSize: 12, color: 'var(--text-secondary)' }}>Duration: <b style={{ color: 'var(--text-primary)' }}>{fmtDur(ex.duration_ms)}</b></Typography>}
                      </Box>
                    </Box>
                  </Box>
                </Box>

                {/* Action row */}
                <Box sx={{ px: '16px', py: '12px', borderTop: '1px solid var(--glass-border)', bgcolor: 'var(--glass-hover)', display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                  {(['skip', 'copy', 'rename', 'overwrite'] as ConflictAction[]).map(a => {
                    const active = item.action === a;
                    const color = a === 'overwrite' ? '#f87171' : a === 'skip' ? 'var(--text-disabled)' : 'var(--accent-light)';
                    return (
                      <Box key={a} component="button"
                        onClick={() => {
                          const patch: Partial<ConflictItem> = { action: a };
                          if (a === 'copy') patch.newName = suggestCopyName(item.localFile.name, existingNames);
                          if (a === 'rename') patch.newName = item.localFile.name;
                          set(item.index, patch);
                        }}
                        sx={{ px: '12px', py: '5px', borderRadius: '8px', border: `1px solid ${active ? color : 'var(--glass-border)'}`, background: active ? `${color}18` : 'none', cursor: 'pointer', color: active ? color : 'var(--text-disabled)', fontSize: 13, fontWeight: active ? 600 : 400, transition: 'all .15s', '&:hover': { borderColor: color, color } }}>
                        {a === 'skip' ? 'Skip' : a === 'copy' ? 'Create copy' : a === 'rename' ? 'Rename' : 'Overwrite'}
                      </Box>
                    );
                  })}
                  {item.action === 'rename' && (
                    <Box sx={{ flex: 1, minWidth: 160 }}>
                      <Box
                        component="input"
                        value={item.newName}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => set(item.index, { newName: e.target.value })}
                        sx={{ width: '100%', boxSizing: 'border-box', background: 'transparent', border: 'none', borderBottom: `1.5px solid ${isDup ? '#f87171' : 'var(--accent)'}`, color: 'var(--text-primary)', fontSize: 13, outline: 'none', pb: '2px' }}
                      />
                      {isDup && <Typography sx={{ fontSize: 11, color: '#f87171', mt: '2px' }}>Already exists</Typography>}
                    </Box>
                  )}
                  {item.action === 'copy' && (
                    <Typography sx={{ fontSize: 12, color: 'var(--text-disabled)', ml: '4px' }}>→ will be saved as <b style={{ color: 'var(--text-secondary)' }}>{item.newName}</b></Typography>
                  )}
                </Box>
              </Box>
            );
          })}
        </Box>

        {/* Footer */}
        <Box sx={{ px: '24px', py: '16px', borderTop: '1px solid var(--glass-border)', display: 'flex', justifyContent: 'flex-end', gap: '10px', flexShrink: 0, bgcolor: 'var(--glass-hover)' }}>
          <Box component="button" onClick={onCancel}
            sx={{ px: '18px', py: '8px', borderRadius: '8px', border: '1px solid var(--glass-border)', background: 'none', cursor: 'pointer', color: 'var(--text-secondary)', fontSize: 14 }}>
            Cancel
          </Box>
          <Box component="button" onClick={() => onResolve(items)} disabled={uploadCount === 0 && nonConflictCount === 0}
            sx={{ px: '18px', py: '8px', borderRadius: '8px', border: 'none', background: (uploadCount > 0 || nonConflictCount > 0) ? 'var(--accent)' : 'var(--glass-hover)', cursor: (uploadCount > 0 || nonConflictCount > 0) ? 'pointer' : 'default', color: '#fff', fontSize: 14, fontWeight: 600, opacity: (uploadCount > 0 || nonConflictCount > 0) ? 1 : 0.5 }}>
            Upload {uploadCount > 0 ? `${uploadCount + nonConflictCount} file${uploadCount + nonConflictCount > 1 ? 's' : ''}` : nonConflictCount > 0 ? `${nonConflictCount} new file${nonConflictCount > 1 ? 's' : ''} (skip duplicates)` : '(all skipped)'}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

// ── Main Page ──────────────────────────────────────────────────────────────


const MediaPage: React.FC = () => {
  const { t } = useTranslation();
  const auth = useSelector((s: RootState) => s.auth.value);
  const { setBreadcrumbs } = usePageHeader();

  const [folders, setFolders] = useState<MediaFolder[]>([]);
  const [files, setFiles] = useState<MediaFile[]>([]);
  const [stats, setStats] = useState<StorageStats | null>(null);
  const [selectedFolderId, setSelectedFolderId] = useState<number | null>(null);
  const [folderPath, setFolderPath] = useState<{ id: number | null; name: string }[]>([{ id: null, name: t('media.allFiles') }]);
  const [selectedFile, setSelectedFile] = useState<MediaFile | null>(null);
  const [checkedIds, setCheckedIds] = useState<Set<number>>(new Set());
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [sizeError, setSizeError] = useState<string[]>([]);
  const sizeErrTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [deleteMsg, setDeleteMsg] = useState<string | null>(null);
  const deleteMsgTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const [search, setSearch] = useState('');
  const [viewMode, setViewMode] = useState<'list' | 'grid'>(
    () => (localStorage.getItem('pv_media_view') as 'list' | 'grid') || 'list'
  );
  const [inlineNewFolder, setInlineNewFolder] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [newFolderLoading, setNewFolderLoading] = useState(false);
  const [playerFile, setPlayerFile] = useState<MediaFile | null>(null);
  const [editorFileId, setEditorFileId] = useState<number | null>(null);
  const [checkedFolderIds, setCheckedFolderIds] = useState<Set<number>>(new Set());
  const [sortKey, setSortKey] = useState<'name' | 'created_at'>('name');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  const [editingFileId, setEditingFileId] = useState<number | null>(null);
  const [editingName, setEditingName] = useState('');
  const [dlState, setDlState] = useState<{ name: string; pct: number; total: number; done: boolean } | null>(null);
  const [conflictItems, setConflictItems] = useState<ConflictItem[] | null>(null);
  const [pendingUpload, setPendingUpload] = useState<{ files: File[]; metaList: FileMeta[] } | null>(null);
  const [usageModal, setUsageModal] = useState<{
    fileName: string;
    playlists: { playlistId: number; playlistName: string; position: number }[];
    screens: { screenId: number; deviceId: string; displayName: string | null }[];
    schedules?: { scheduleId: number; scheduleName: string; eventName: string }[];
    transcoding?: boolean;
    blockedCount?: number;
  } | null>(null);

  const [draggedFileIds, setDraggedFileIds] = useState<number[]>([]);
  const [draggedFolderId, setDraggedFolderId] = useState<number | null>(null);
  const [dragTargetFolderId, setDragTargetFolderId] = useState<number | null>(null);
  const [movePanelOpen, setMovePanelOpen] = useState(false);
  const moveBtnRef = useRef<HTMLButtonElement>(null);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const dlTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const fileLoadGen = useRef(0);

  const loadFolders = useCallback(async () => {
    try {
      const { data } = await axios.get<MediaFolder[]>('/media/folders');
      setFolders(data);
    } catch { /* handled by interceptor */ }
  }, []);

  const loadFiles = useCallback(async (folderId: number | null) => {
    setFiles([]);
    setLoading(true);
    const gen = ++fileLoadGen.current;
    try {
      // 'root' — только файлы вне папок; пустой параметр вернул бы вообще все
      const params = folderId != null ? { folder_id: folderId } : { folder_id: 'root' };
      const { data } = await axios.get<MediaFile[]>('/media/files', { params });
      if (gen !== fileLoadGen.current) return;
      setFiles(data);
    } catch { /* handled by interceptor */ }
    finally { if (gen === fileLoadGen.current) setLoading(false); }
  }, []);

  const loadStats = useCallback(async () => {
    try {
      const { data } = await axios.get<StorageStats>('/media/storage-stats');
      setStats(data);
    } catch { /* handled by interceptor */ }
  }, []);

  const handleDownload = useCallback(async (fileId: number, fileName: string) => {
    if (dlTimerRef.current) clearTimeout(dlTimerRef.current);
    setDlState({ name: fileName, pct: 0, total: 0, done: false });
    try {
      const res = await fetch(`${API_BASE}/media/download/${fileId}`);
      if (!res.ok || !res.body) throw new Error('Failed');
      const contentLength = res.headers.get('Content-Length');
      const total = contentLength ? parseInt(contentLength, 10) : 0;
      const reader = res.body.getReader();
      const chunks: Uint8Array[] = [];
      let received = 0;
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        chunks.push(value);
        received += value.length;
        setDlState({ name: fileName, pct: total > 0 ? Math.round((received / total) * 100) : 0, total, done: false });
      }
      const blob = new Blob(chunks);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      setDlState({ name: fileName, pct: 100, total, done: true });
      dlTimerRef.current = setTimeout(() => setDlState(null), 2500);
    } catch {
      setDlState(null);
    }
  }, []);

  useEffect(() => {
    if (!auth) return;
    loadFolders();
    loadStats();
  }, [auth, loadFolders, loadStats]);

  useEffect(() => {
    if (!auth) return;
    loadFiles(selectedFolderId);
    setCheckedIds(new Set());
  }, [auth, selectedFolderId, loadFiles]);

  // Auto-refresh every 5s while any file is being transcoded
  useEffect(() => {
    const hasTranscoding = files.some(f => f.transcoding);
    if (!hasTranscoding) return;
    const timer = setInterval(() => loadFiles(selectedFolderId), 5000);
    return () => clearInterval(timer);
  }, [files, selectedFolderId, loadFiles]);

  const handleFolderOpen = (id: number, name: string) => {
    setSelectedFolderId(id);
    setSelectedFile(null);
    setCheckedIds(new Set());
    setCheckedFolderIds(new Set());
    setFolderPath(prev => {
      const exists = prev.findIndex(s => s.id === id);
      if (exists >= 0) return prev.slice(0, exists + 1);
      return [...prev, { id, name }];
    });
  };

  const createFolder = async () => {
    const name = newFolderName.trim();
    if (!name) return;
    if (visibleFolders.some(f => f.name.toLowerCase() === name.toLowerCase())) return;
    setNewFolderLoading(true);
    try {
      await axios.post('/media/folders', { name, parent_id: selectedFolderId });
      await loadFolders();
      setInlineNewFolder(false);
      setNewFolderName('');
    } catch { /* handled by interceptor */ }
    finally { setNewFolderLoading(false); }
  };

  type FolderDeleteResult = {
    deleted: number;
    blocked: {
      id: number; originalName: string; reason: string;
      usages: {
        playlists: { playlistId: number; playlistName: string; position: number }[];
        screens: { screenId: number; deviceId: string; displayName: string | null }[];
        schedules: { scheduleId: number; scheduleName: string; eventName: string }[];
      };
    }[];
  } | false;

  const toggleFolderCheck = (id: number) => {
    setCheckedFolderIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const deleteFolder = useCallback(async (id: number) => {
    try {
      // Папка не удаляется, если внутри есть занятые файлы — показываем чем заняты
      const { data } = await axios.delete<FolderDeleteResult>(`/media/folders/${id}`);
      if (data && data.blocked && data.blocked.length > 0) {
        const first = data.blocked[0];
        setUsageModal({
          fileName: first.originalName,
          playlists: first.usages.playlists,
          screens: first.usages.screens,
          schedules: first.usages.schedules,
          transcoding: first.reason === 'TRANSCODING',
          blockedCount: data.blocked.length,
        });
      }
      setCheckedFolderIds(prev => { const n = new Set(prev); n.delete(id); return n; });
      await loadFolders();
      await loadFiles(selectedFolderId);
      await loadStats();
    } catch { /* handled by interceptor */ }
  }, [selectedFolderId, loadFolders, loadFiles, loadStats]);

  const showDeleteMsg = (msg: string) => {
    setDeleteMsg(msg);
    if (deleteMsgTimer.current) clearTimeout(deleteMsgTimer.current);
    deleteMsgTimer.current = setTimeout(() => setDeleteMsg(null), 5000);
  };

  const deleteFile = useCallback(async (id: number) => {
    try {
      const { data } = await axios.delete<boolean>(`/media/files/${id}`);
      if (data === false) {
        showDeleteMsg('File not found — it may have already been deleted.');
        await loadFiles(selectedFolderId);
        return;
      }
      setSelectedFile(prev => prev?.id === id ? null : prev);
      await loadFiles(selectedFolderId);
      await loadStats();
    } catch (err: any) {
      const body = err?.response?.data;
      if (err?.response?.status === 409 && body?.code === 'IN_USE') {
        const file = files.find(f => f.id === id);
        setUsageModal({ fileName: file?.original_name ?? `#${id}`, ...body.usages });
      } else if (err?.response?.status === 409 && body?.code === 'TRANSCODING') {
        setUsageModal({ fileName: files.find(f => f.id === id)?.original_name ?? `#${id}`, playlists: [], screens: [], schedules: [], transcoding: true });
      } else {
        const msg = body?.message ?? err?.message ?? 'Delete failed';
        showDeleteMsg(typeof msg === 'string' ? msg : JSON.stringify(msg));
        console.error('[deleteFile]', err?.response?.status, body);
      }
    }
  }, [selectedFolderId, loadFiles, loadStats, files]);

  const deleteSelected = useCallback(async () => {
    const fileIds = Array.from(checkedIds);
    const folderIds = Array.from(checkedFolderIds);
    if (!fileIds.length && !folderIds.length) return;
    try {
      if (fileIds.length) {
        const { data } = await axios.delete<{ deleted: number; blocked: { id: number; originalName: string; reason: string; usages: { playlists: { playlistId: number; playlistName: string; position: number }[]; screens: { screenId: number; deviceId: string; displayName: string | null }[]; schedules: { scheduleId: number; scheduleName: string; eventName: string }[] } }[] }>('/media/files', { data: { ids: fileIds } });
        if (data.blocked.length > 0) {
          // Show modal for first blocked file, rest can be seen in sequence
          const first = data.blocked[0];
          setUsageModal({
            fileName: first.originalName,
            playlists: first.usages.playlists,
            screens: first.usages.screens,
            schedules: first.usages.schedules,
            transcoding: first.reason === 'TRANSCODING',
            blockedCount: data.blocked.length,
          });
        }
      }
      for (const fid of folderIds) {
        const { data } = await axios.delete<FolderDeleteResult>(`/media/folders/${fid}`);
        if (data && data.blocked && data.blocked.length > 0) {
          const first = data.blocked[0];
          setUsageModal({
            fileName: first.originalName,
            playlists: first.usages.playlists,
            screens: first.usages.screens,
            schedules: first.usages.schedules,
            transcoding: first.reason === 'TRANSCODING',
            blockedCount: data.blocked.length,
          });
        }
      }
      setCheckedIds(new Set());
      setCheckedFolderIds(new Set());
      setSelectedFile(prev => (prev && fileIds.includes(prev.id)) ? null : prev);
      await loadFolders();
      await loadFiles(selectedFolderId);
      await loadStats();
    } catch { /* handled by interceptor */ }
  }, [checkedIds, checkedFolderIds, selectedFolderId, loadFolders, loadFiles, loadStats]);

  const bulkMoveFiles = useCallback(async (targetFolderId: number | null) => {
    const ids = draggedFileIds.length > 0 ? draggedFileIds : Array.from(checkedIds);
    if (!ids.length) return;
    try {
      await axios.patch('/media/files/bulk-move', { fileIds: ids, folderId: targetFolderId });
      setCheckedIds(new Set());
      setDraggedFileIds([]);
      setMovePanelOpen(false);
      await loadFiles(selectedFolderId);
      await loadFolders();
      await loadStats();
    } catch { /* handled by interceptor */ }
  }, [draggedFileIds, checkedIds, selectedFolderId, loadFiles, loadFolders, loadStats]);

  const moveFolderTo = useCallback(async (id: number, parentId: number | null) => {
    try {
      await axios.patch(`/media/folders/${id}/move`, { parent_id: parentId });
      await loadFolders();
    } catch { /* handled by interceptor */ }
  }, [loadFolders]);

  const handleBreadcrumb = (idx: number, seg: { id: number | null; name: string }) => {
    if (seg.id === null) {
      setSelectedFolderId(null);
      setFolderPath([{ id: null, name: t('media.allFiles') }]);
    } else {
      setSelectedFolderId(seg.id);
      setFolderPath(prev => prev.slice(0, idx + 1));
    }
    setSelectedFile(null);
    setCheckedIds(new Set());
    setCheckedFolderIds(new Set());
  };

  // Sync folder breadcrumbs into the Topbar — skip the root "All Files" segment
  useEffect(() => {
    const root = folderPath[0]; // { id: null, name: "All Files" }
    const nested = folderPath.slice(1); // actual folder segments
    const segs = [
      {
        label: t('nav.media'),
        onClick: nested.length > 0 ? () => handleBreadcrumb(0, root) : undefined,
      },
      ...nested.map((seg, i) => ({
        label: seg.name,
        onClick: i < nested.length - 1 ? () => handleBreadcrumb(i + 1, seg) : undefined,
      })),
    ];
    setBreadcrumbs(segs);
    return () => setBreadcrumbs([]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [folderPath, t]);

  const probeMetadata = (files: File[]) =>
    Promise.all(files.map(async file => {
      const url = URL.createObjectURL(file);
      if (file.type.startsWith('video/')) {
        const [baseMeta, codec] = await Promise.all([
          new Promise<FileMeta>(resolve => {
            const v = document.createElement('video');
            v.preload = 'metadata';
            v.muted = true;
            v.onerror = () => { URL.revokeObjectURL(url); resolve({}); };
            v.onloadedmetadata = () => {
              const dur = Math.round(v.duration * 1000);
              const w = v.videoWidth, h = v.videoHeight;
              if (!isFinite(v.duration) || v.duration === 0) {
                URL.revokeObjectURL(url); resolve({ durationMs: dur, width: w, height: h }); return;
              }
              const timer = setTimeout(() => { URL.revokeObjectURL(url); resolve({ durationMs: dur, width: w, height: h }); }, 5000);
              v.onseeked = () => {
                clearTimeout(timer);
                v.onseeked = null;
                const scale = Math.min(1, 640 / w);
                const canvas = document.createElement('canvas');
                canvas.width = Math.round(w * scale);
                canvas.height = Math.round(h * scale);
                const ctx = canvas.getContext('2d');
                if (!ctx) { URL.revokeObjectURL(url); resolve({ durationMs: dur, width: w, height: h }); return; }
                ctx.drawImage(v, 0, 0, canvas.width, canvas.height);
                canvas.toBlob(blob => {
                  URL.revokeObjectURL(url);
                  resolve({ durationMs: dur, width: w, height: h, thumbnail: blob ?? undefined });
                }, 'image/jpeg', 0.82);
              };
              v.currentTime = Math.min(2, v.duration * 0.25);
            };
            v.src = url;
          }),
          probeVideoCodec(file),
        ]);
        return { ...baseMeta, ...codec };
      } else if (file.type.startsWith('image/')) {
        return new Promise<FileMeta>(resolve => {
          const img = new Image();
          img.onload = () => { resolve({ width: img.naturalWidth, height: img.naturalHeight }); URL.revokeObjectURL(url); };
          img.onerror = () => { resolve({}); URL.revokeObjectURL(url); };
          img.src = url;
        });
      } else {
        URL.revokeObjectURL(url);
        return {} as FileMeta;
      }
    }));

  const doUpload = useCallback(async (
    files: File[],
    metaList: FileMeta[],
    names: (string | null)[],
    overwriteIds: number[],
  ) => {
    setUploading(true);
    setUploadProgress(0);
    try {
      const formData = new FormData();
      files.forEach(f => formData.append('files', f));
      if (selectedFolderId != null) formData.append('folder_id', String(selectedFolderId));
      // Strip thumbnail blobs from JSON metadata, append them separately
      formData.append('metadata', JSON.stringify(metaList.map(({ thumbnail: _t, ...rest }) => rest)));
      formData.append('names', JSON.stringify(names));
      if (overwriteIds.length) formData.append('overwrite_ids', JSON.stringify(overwriteIds));
      // Append thumbnails with index mapping so backend knows which file each thumb belongs to
      const thumbIndices: number[] = [];
      metaList.forEach((m, i) => {
        if (m.thumbnail) { formData.append('thumbnails', m.thumbnail, 'thumb.jpg'); thumbIndices.push(i); }
      });
      if (thumbIndices.length) formData.append('thumbnail_indices', JSON.stringify(thumbIndices));
      await axios.post('/media/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (e) => { if (e.total) setUploadProgress(Math.round((e.loaded / e.total) * 100)); },
      });
      setUploadProgress(100);
      await loadFiles(selectedFolderId);
      await loadStats();
    } catch { /* interceptor */ }
    finally { setUploading(false); setUploadProgress(0); }
  }, [selectedFolderId, loadFiles, loadStats]);

  const proceedWithUpload = useCallback(async (resolved: ConflictItem[]) => {
    setConflictItems(null);
    if (!pendingUpload) return;
    const { files: allFiles, metaList: allMeta } = pendingUpload;
    setPendingUpload(null);

    const toUpload: File[] = [];
    const toMeta: FileMeta[] = [];
    const names: (string | null)[] = [];
    const overwriteIds: number[] = [];

    for (let i = 0; i < allFiles.length; i++) {
      const conflict = resolved.find(r => r.index === i);
      if (!conflict) {
        toUpload.push(allFiles[i]); toMeta.push(allMeta[i]); names.push(null);
      } else if (conflict.action === 'skip') {
        // skip — don't add
      } else {
        toUpload.push(allFiles[i]); toMeta.push(allMeta[i]);
        if (conflict.action === 'overwrite') { names.push(null); overwriteIds.push(conflict.existing.id); }
        else { names.push(conflict.newName.trim() || null); }
      }
    }
    if (!toUpload.length) return;
    await doUpload(toUpload, toMeta, names, overwriteIds);
  }, [pendingUpload, doUpload]);

  const uploadFiles = useCallback(async (fileList: FileList) => {
    if (!fileList.length) return;
    const allFiles = Array.from(fileList);
    const oversized = allFiles.filter(f => f.size > MAX_FILE_BYTES);
    const files = allFiles.filter(f => f.size <= MAX_FILE_BYTES);
    if (oversized.length) {
      if (sizeErrTimer.current) clearTimeout(sizeErrTimer.current);
      setSizeError(oversized.map(f => f.name));
      sizeErrTimer.current = setTimeout(() => setSizeError([]), 5000);
    }
    if (!files.length) return;
    const metaList = await probeMetadata(files);

    // Check duplicates before upload
    try {
      const { data } = await axios.post<{ conflicts: { index: number; existing: ConflictExisting }[] }>('/media/check-duplicates', {
        files: files.map(f => ({ name: f.name, size: f.size })),
        ...(selectedFolderId != null ? { folder_id: selectedFolderId } : {}),
      });
      if (data.conflicts.length > 0) {
        const conflictList: ConflictItem[] = data.conflicts.map(c => ({
          index: c.index,
          localFile: files[c.index],
          localMeta: metaList[c.index],
          existing: c.existing,
          action: 'skip' as ConflictAction,
          newName: suggestCopyName(files[c.index].name, files.map(f => f.name)),
        }));
        setPendingUpload({ files, metaList });
        setConflictItems(conflictList);
        return;
      }
    } catch { /* if validation fails — upload without it */ }

    await doUpload(files, metaList, files.map(() => null), []);
  }, [selectedFolderId, doUpload]);

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) uploadFiles(e.target.files);
    e.target.value = '';
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    if (e.dataTransfer.files) uploadFiles(e.dataTransfer.files);
  };

  const toggleCheck = (id: number) => {
    setCheckedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  // Folders visible at current level
  const visibleFolders = selectedFolderId === null
    ? folders.filter(f => f.parent_id === null)
    : folders.filter(f => f.parent_id === selectedFolderId);

  const renameFolder = useCallback(async (id: number, name: string) => {
    try {
      await axios.patch(`/media/folders/${id}`, { name });
      await loadFolders();
    } catch { /* handled by interceptor */ }
  }, [loadFolders]);

  const commitRenameFile = useCallback(async (id: number, name: string) => {
    const trimmed = name.trim();
    setEditingFileId(null);
    if (!trimmed) return;
    if (files.some(f => f.id !== id && f.original_name.toLowerCase() === trimmed.toLowerCase())) return;
    try {
      await axios.patch(`/media/files/${id}`, { name: trimmed });
      await loadFiles(selectedFolderId);
    } catch { /* handled by interceptor */ }
  }, [files, loadFiles, selectedFolderId]);

  const filteredFiles = (search ? files.filter(f => f.original_name.toLowerCase().includes(search.toLowerCase())) : files)
    .slice()
    .sort((a, b) => {
      const mul = sortDir === 'asc' ? 1 : -1;
      if (sortKey === 'name') return mul * a.original_name.localeCompare(b.original_name);
      return mul * (new Date(a.created_at).getTime() - new Date(b.created_at).getTime());
    });
  const newFolderDuplicate = newFolderName.trim() !== '' && visibleFolders.some(f => f.name.toLowerCase() === newFolderName.trim().toLowerCase());
  const fileNameDuplicate = editingFileId !== null && editingName.trim() !== '' && files.some(f => f.id !== editingFileId && f.original_name.toLowerCase() === editingName.trim().toLowerCase());

  const allEverythingChecked = filteredFiles.length + visibleFolders.length > 0
    && checkedIds.size === filteredFiles.length
    && checkedFolderIds.size === visibleFolders.length;
  const toggleAll = () => {
    if (allEverythingChecked) {
      setCheckedIds(new Set());
      setCheckedFolderIds(new Set());
    } else {
      setCheckedIds(new Set(filteredFiles.map(f => f.id)));
      setCheckedFolderIds(new Set(visibleFolders.map(f => f.id)));
    }
  };

  const totalItems = visibleFolders.length + filteredFiles.length;
  return (
    <Box sx={{ display: 'flex', height: 'calc(var(--app-h) - 52px)', overflow: 'hidden', p: { xs: 0, md: '16px' }, gap: '12px' }}>

      {/* ── LEFT ISLAND: browser + content ── */}
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', background: 'var(--glass-bg)', backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)', border: { xs: 'none', md: '1px solid var(--glass-border)' }, borderRadius: { xs: 0, md: '14px' } }}>

      {/* ── HEADER ── */}
      <Box sx={{ flexShrink: 0, borderBottom: '1px solid var(--divider)' }}>

        {/* Desktop header row */}
        <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', gap: '8px', px: '18px', py: '9px' }}>
          {/* search */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '7px', ...glassSx, borderRadius: '9px', px: '10px', py: '6px', width: 200, '&:focus-within': { borderColor: 'var(--accent)' } }}>
            <Box sx={{ color: 'var(--text-disabled)', display: 'flex', flexShrink: 0 }}><IcoSearch /></Box>
            <Box component="input" value={search} onChange={e => setSearch(e.target.value)} placeholder={t('media.searchPlaceholder')} sx={{ background: 'none', border: 'none', outline: 'none', color: 'var(--text-primary)', fontSize: 13, flex: 1, minWidth: 0, '&::placeholder': { color: 'var(--text-disabled)' } }} />
          </Box>
          <Box component="button" onClick={() => { setInlineNewFolder(true); setNewFolderName(suggestFolderName('New folder', visibleFolders.map(f => f.name))); }} sx={btnSecondarySx}>
            <IcoPlus size={11} /> {t('media.newFolder')}
          </Box>
          <Box component="button" onClick={() => fileInputRef.current?.click()} sx={btnPrimarySx}>
            <IcoUpload size={13} />
            {uploading ? `${uploadProgress}%` : t('media.uploadFiles')}
          </Box>
          <Box sx={{ display: 'flex', ...glassSx, borderRadius: '9px', overflow: 'hidden', p: '2px', gap: '2px' }}>
            {[{ icon: <IcoList />, mode: 'list' as const }, { icon: <IcoGrid />, mode: 'grid' as const }].map(({ icon, mode }) => (
              <Box key={mode} component="button" onClick={() => { setViewMode(mode); localStorage.setItem('pv_media_view', mode); }} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 30, height: 30, borderRadius: '7px', cursor: 'pointer', bgcolor: viewMode === mode ? 'var(--selected-bg)' : 'none', border: 'none', color: viewMode === mode ? 'var(--accent-light)' : 'var(--text-disabled)', '&:hover': { bgcolor: 'var(--glass-hover)', color: 'var(--text-primary)' } }}>
                {icon}
              </Box>
            ))}
          </Box>
        </Box>

        {/* Mobile header: search + new folder + upload */}
        <Box sx={{ display: { xs: 'flex', md: 'none' }, alignItems: 'center', gap: '8px', px: '14px', pt: '10px', pb: '10px' }}>
          <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', gap: '7px', ...glassSx, borderRadius: '9px', px: '10px', py: '6px', '&:focus-within': { borderColor: 'var(--accent)' } }}>
            <Box sx={{ color: 'var(--text-disabled)', display: 'flex', flexShrink: 0 }}><IcoSearch /></Box>
            <Box component="input" value={search} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearch(e.target.value)} placeholder={t('media.searchPlaceholder')} sx={{ background: 'none', border: 'none', outline: 'none', color: 'var(--text-primary)', fontSize: 13, flex: 1, minWidth: 0, '&::placeholder': { color: 'var(--text-disabled)' } }} />
          </Box>
          <Box component="button" onClick={() => { setInlineNewFolder(true); setNewFolderName(suggestFolderName('New folder', visibleFolders.map(f => f.name))); }} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 34, height: 34, borderRadius: '9px', ...glassSx, color: 'var(--text-secondary)', cursor: 'pointer', flexShrink: 0 }}>
            <IcoPlus size={13} />
          </Box>
          <Box component="button" onClick={() => fileInputRef.current?.click()} sx={{ ...btnPrimarySx, flexShrink: 0 }}>
            <IcoUpload size={11} />
            {uploading ? `${uploadProgress}%` : t('media.uploadFiles')}
          </Box>
        </Box>

        {/* Mobile header row 2: storage bar */}
        {stats && (
          <Box sx={{ display: { xs: 'block', md: 'none' }, px: '14px', pb: '10px' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: '4px' }}>
              <Typography sx={{ fontSize: 10, color: 'var(--text-disabled)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.06em' }}>Storage</Typography>
              <Typography sx={{ fontSize: 10, color: 'var(--text-disabled)', fontFamily: '"JetBrains Mono", monospace' }}>
                {(stats.used_bytes / 1024 / 1024).toFixed(1)} / {(stats.limit_bytes / 1024 / 1024).toFixed(0)} MB
              </Typography>
            </Box>
            <Box sx={{ height: 4, borderRadius: 2, bgcolor: 'var(--glass-border)', overflow: 'hidden' }}>
              <Box sx={{ height: '100%', width: `${Math.min(100, (stats.used_bytes / stats.limit_bytes) * 100)}%`, bgcolor: (stats.used_bytes / stats.limit_bytes) > 0.85 ? '#f87171' : 'var(--accent)', borderRadius: 2, transition: 'width .3s' }} />
            </Box>
          </Box>
        )}
      </Box>

      {/* ── MOBILE UNIFIED LIST (xs only) ── */}
      <Box
        sx={{ display: { xs: 'flex', md: 'none' }, flex: 1, flexDirection: 'column', overflow: 'auto', px: '12px', pb: '8px', gap: '2px', pt: '6px' }}
        onTouchStart={e => { (e.currentTarget as any)._ptr = e.touches[0].clientY; }}
        onTouchEnd={e => {
          const el = e.currentTarget as HTMLDivElement;
          const start = (el as any)._ptr ?? 0;
          const delta = (e.changedTouches[0]?.clientY ?? 0) - start;
          if (el.scrollTop === 0 && delta > 70) { loadFolders(); loadFiles(selectedFolderId); }
        }}
      >
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress size={24} sx={{ color: 'var(--accent)' }} />
          </Box>
        ) : (
          <>
            {/* Inline new folder input */}
            {inlineNewFolder && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', px: '12px', py: '10px', borderRadius: '10px', border: '1px solid var(--accent)', bgcolor: 'rgba(99,102,241,.06)', mb: '2px' }}>
                <Box sx={{ fontSize: 18, lineHeight: 1, flexShrink: 0 }}>📁</Box>
                <Box component="input" autoFocus value={newFolderName}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNewFolderName(e.target.value)}
                  onKeyDown={(e: React.KeyboardEvent) => { if (e.key === 'Enter') createFolder(); if (e.key === 'Escape') { setInlineNewFolder(false); setNewFolderName(''); } }}
                  sx={{ flex: 1, background: 'none', border: 'none', outline: 'none', color: 'var(--text-primary)', fontSize: 13, fontWeight: 600, fontFamily: 'inherit', minWidth: 0 }}
                />
                <Box component="button" onClick={createFolder}
                  sx={{ px: '10px', py: '4px', borderRadius: '7px', fontSize: 12, fontWeight: 600, fontFamily: 'inherit', cursor: 'pointer', bgcolor: 'var(--accent)', border: 'none', color: '#fff', flexShrink: 0 }}>
                  {t('media.create')}
                </Box>
                <Box component="button" onClick={() => { setInlineNewFolder(false); setNewFolderName(''); }}
                  sx={{ width: 26, height: 26, display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '7px', cursor: 'pointer', bgcolor: 'transparent', border: '1px solid var(--glass-border)', color: 'var(--text-disabled)', flexShrink: 0, fontFamily: 'inherit' }}>
                  ✕
                </Box>
              </Box>
            )}
            {/* Folders A→Z */}
            {[...visibleFolders].sort((a, b) => a.name.localeCompare(b.name)).map(folder => (
              <Box key={`f-${folder.id}`} onClick={() => handleFolderOpen(folder.id, folder.name)} sx={{ display: 'flex', alignItems: 'center', gap: '10px', px: '12px', py: '10px', borderRadius: '10px', bgcolor: 'var(--glass-hover)', border: '1px solid var(--glass-border)', cursor: 'pointer', '&:active': { opacity: .75 } }}>
                <Box sx={{ fontSize: 20, lineHeight: 1, flexShrink: 0 }}>📁</Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography sx={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {folder.name}
                  </Typography>
                  {folder.file_count != null && (
                    <Typography sx={{ fontSize: 11, color: 'var(--text-disabled)' }}>{folder.file_count} items</Typography>
                  )}
                </Box>
                <Box sx={{ fontSize: 16, color: 'var(--text-disabled)' }}>›</Box>
              </Box>
            ))}
            {/* Files A→Z */}
            {[...filteredFiles].sort((a, b) => a.original_name.localeCompare(b.original_name)).map(file => {
              const ext = file.original_name.split('.').pop()?.toLowerCase() ?? '';
              const isImg = ['jpg','jpeg','png','gif','webp','svg','bmp','ico'].includes(ext);
              const isVid = ['mp4','mov','avi','webm','mkv'].includes(ext);
              const icon = isImg ? '🖼' : isVid ? '🎬' : '📄';
              const sizeMb = file.size_bytes != null ? (file.size_bytes / 1024 / 1024).toFixed(1) + ' MB' : '';
              return (
                <Box key={`file-${file.id}`} sx={{ display: 'flex', alignItems: 'center', gap: '10px', px: '12px', py: '10px', borderRadius: '10px', bgcolor: 'var(--glass-hover)', border: '1px solid var(--glass-border)' }}>
                  <Box sx={{ fontSize: 20, lineHeight: 1, flexShrink: 0 }}>{icon}</Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{ fontSize: 13, fontWeight: 500, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {file.original_name}
                    </Typography>
                    {sizeMb && <Typography sx={{ fontSize: 11, color: 'var(--text-disabled)' }}>{sizeMb}</Typography>}
                  </Box>
                </Box>
              );
            })}
            {visibleFolders.length === 0 && filteredFiles.length === 0 && !loading && (
              <Box sx={{ py: 6, textAlign: 'center' }}>
                <Typography sx={{ fontSize: 13, color: 'var(--text-disabled)' }}>No files yet</Typography>
              </Box>
            )}
          </>
        )}
      </Box>

      {/* ── CONTENT SPLIT (desktop only) ── */}
      <Box sx={{ display: { xs: 'none', md: 'flex' }, flex: 1, overflow: 'hidden' }}>

        {/* ── MAIN CONTENT ── */}
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0, overflow: 'hidden', position: 'relative' }}>

        {/* ── LIST HEADER — static, never scrolls ── */}
        {!loading && viewMode === 'list' && (
          <Box sx={{ px: 0, flexShrink: 0, borderBottom: '1px solid var(--divider)', ...glassSx, borderRadius: 0 }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
              <colgroup>
                <col style={{ width: 44 }} />
                <col />
                <col style={{ width: 90 }} />
                <col style={{ width: 80 }} />
                <col style={{ width: 90 }} />
                <col style={{ width: 120 }} />
                <col style={{ width: 50 }} />
              </colgroup>
              <thead>
                <tr>
                  <th style={{ width: 44, padding: '9px 10px 9px 18px', textAlign: 'left' }}>
                    <GlassCheckbox checked={allEverythingChecked} onChange={toggleAll} />
                  </th>
                  {/* sortable Name */}
                  <th
                    onClick={() => { if (sortKey === 'name') setSortDir(d => d === 'asc' ? 'desc' : 'asc'); else { setSortKey('name'); setSortDir('asc'); } }}
                    style={{ padding: '9px 12px', textAlign: 'left', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.08em', color: sortKey === 'name' ? 'var(--accent-light)' : 'var(--text-disabled)', whiteSpace: 'nowrap', cursor: 'pointer', userSelect: 'none' }}
                  >
                    {t('media.colName')} {sortKey === 'name' ? (sortDir === 'asc' ? '↑' : '↓') : <span style={{ opacity: 0.3 }}>↕</span>}
                  </th>
                  <th style={{ padding: '9px 12px', textAlign: 'left', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.08em', color: 'var(--text-disabled)', whiteSpace: 'nowrap' }}>
                    {t('media.colStatus')}
                  </th>
                  <th style={{ padding: '9px 12px', textAlign: 'left', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.08em', color: 'var(--text-disabled)', whiteSpace: 'nowrap' }}>
                    {t('media.colSize')}
                  </th>
                  <th style={{ padding: '9px 12px', textAlign: 'left', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.08em', color: 'var(--text-disabled)', whiteSpace: 'nowrap' }}>
                    {t('media.colResolution')}
                  </th>
                  {/* sortable Date Added */}
                  <th
                    onClick={() => { if (sortKey === 'created_at') setSortDir(d => d === 'asc' ? 'desc' : 'asc'); else { setSortKey('created_at'); setSortDir('desc'); } }}
                    style={{ padding: '9px 12px', textAlign: 'left', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.08em', color: sortKey === 'created_at' ? 'var(--accent-light)' : 'var(--text-disabled)', whiteSpace: 'nowrap', cursor: 'pointer', userSelect: 'none' }}
                  >
                    {t('media.colCreatedAt')} {sortKey === 'created_at' ? (sortDir === 'asc' ? '↑' : '↓') : <span style={{ opacity: 0.3 }}>↕</span>}
                  </th>
                  <th />
                </tr>
              </thead>
            </table>
          </Box>
        )}

        {/* Content area — scrollable body */}
        <Box
          sx={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', pb: '8px', position: 'relative',
            '&::-webkit-scrollbar': { width: 5 },
            '&::-webkit-scrollbar-track': { background: 'transparent' },
            '&::-webkit-scrollbar-thumb': { background: 'rgba(255,255,255,0.13)', borderRadius: 3 },
            '&::-webkit-scrollbar-thumb:hover': { background: 'rgba(255,255,255,0.22)' },
            scrollbarWidth: 'thin', scrollbarColor: 'rgba(255,255,255,0.13) transparent',
          }}
          onDragOver={e => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={e => { if (!e.currentTarget.contains(e.relatedTarget as Node)) setDragOver(false); }}
          onDrop={handleDrop}
        >
          {dragOver && (
            <Box sx={{
              position: 'absolute', inset: 0, zIndex: 50, pointerEvents: 'none',
              border: '2px dashed var(--accent)', borderRadius: '10px',
              bgcolor: 'rgba(99,102,241,.09)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <Box sx={{ textAlign: 'center', color: 'var(--accent)' }}>
                <IcoUpload size={36} />
                <Typography sx={{ mt: 1, fontSize: 16, fontWeight: 600, color: 'var(--accent)' }}>
                  Drop files to upload
                </Typography>
              </Box>
            </Box>
          )}

          {/* Watermark — only when folder is completely empty */}
          {!loading && !search && visibleFolders.length === 0 && filteredFiles.length === 0 && (
            <Box sx={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '14px', pointerEvents: 'none', zIndex: 0 }}>
              <Box sx={{ opacity: .08, color: 'var(--text-primary)' }}>
                <IcoUpload size={56} />
              </Box>
              <Typography sx={{ fontSize: 15, fontWeight: 600, color: 'var(--text-primary)', opacity: .12, letterSpacing: '.02em' }}>
                {t('media.dropZoneMain')}
              </Typography>
              <Typography sx={{ fontSize: 12, color: 'var(--text-primary)', opacity: .07, mt: '-8px' }}>
                {t('media.dropZoneHint')}
              </Typography>
            </Box>
          )}
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
              <CircularProgress size={28} sx={{ color: 'var(--accent)' }} />
            </Box>
          ) : viewMode === 'grid' ? (
            /* ─── GRID VIEW ─── */
            <>
              {(visibleFolders.length > 0 || inlineNewFolder) && (
                <>
                  {(filteredFiles.length > 0 || inlineNewFolder) && (
                    <Typography sx={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.1em', color: 'var(--text-disabled)', mt: '12px', mb: '8px' }}>
                      Folders
                    </Typography>
                  )}
                  <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))', gap: '10px', mb: '16px' }}>
                    {inlineNewFolder && (
                      <Box sx={{ aspectRatio: '1', borderRadius: '12px', overflow: 'hidden', position: 'relative', background: 'linear-gradient(145deg, var(--folder-card-from) 0%, var(--folder-card-to) 100%)', border: '1.5px solid var(--accent)' }}>
                        <Box sx={{ position: 'absolute', bottom: 0, left: 0, right: 0, p: '8px 12px 10px', background: 'var(--glass-bg)', backdropFilter: 'blur(20px)' }}>
                          <Box
                            component="input" autoFocus value={newFolderName}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNewFolderName(e.target.value)}
                            onKeyDown={(e: React.KeyboardEvent) => {
                              if (e.key === 'Enter') createFolder();
                              if (e.key === 'Escape') { setInlineNewFolder(false); setNewFolderName(''); }
                            }}
                            onFocus={(e: React.FocusEvent<HTMLInputElement>) => e.target.select()}
                            placeholder="Folder name"
                            sx={{ width: '100%', boxSizing: 'border-box', background: 'transparent', border: 'none', borderBottom: `1.5px solid ${newFolderDuplicate ? '#f87171' : 'var(--accent)'}`, color: 'var(--text-primary)', fontSize: 13, outline: 'none', pb: '2px', '&::placeholder': { color: 'var(--text-disabled)' } }}
                          />
                          {newFolderDuplicate
                            ? <Typography sx={{ fontSize: 10, color: '#f87171', mt: '3px' }}>Already exists</Typography>
                            : <Typography sx={{ fontSize: 10, color: 'var(--text-disabled)', mt: '3px' }}>Enter — save · Esc — cancel</Typography>
                          }
                        </Box>
                      </Box>
                    )}
                    {visibleFolders.map(f => (
                      <MediaFolderCard
                        key={f.id}
                        folder={f}
                        onClick={() => handleFolderOpen(f.id, f.name)}
                        checked={checkedFolderIds.has(f.id)}
                        onCheck={() => toggleFolderCheck(f.id)}
                        menuSlot={<FolderActionMenu folderId={f.id} folderName={f.name} existingNames={visibleFolders.filter(x => x.id !== f.id).map(x => x.name)} onDelete={deleteFolder} onRename={renameFolder} />}
                        draggable
                        onDragStart={e => { setDraggedFolderId(f.id); e.dataTransfer.effectAllowed = 'move'; e.dataTransfer.setData('pv-folder', String(f.id)); }}
                        onDragEnd={() => { setDraggedFolderId(null); setDragTargetFolderId(null); }}
                        isDragTarget={dragTargetFolderId === f.id && draggedFolderId !== f.id}
                        onDragOver={e => { e.preventDefault(); e.stopPropagation(); if (draggedFolderId !== f.id) setDragTargetFolderId(f.id); }}
                        onDragLeave={() => setDragTargetFolderId(null)}
                        onDrop={e => {
                          e.preventDefault(); e.stopPropagation(); setDragTargetFolderId(null);
                          if (e.dataTransfer.types.includes('pv-folder')) {
                            const srcId = Number(e.dataTransfer.getData('pv-folder'));
                            if (srcId && srcId !== f.id) moveFolderTo(srcId, f.id);
                            setDraggedFolderId(null);
                          } else { bulkMoveFiles(f.id); }
                        }}
                      />
                    ))}
                  </Box>
                </>
              )}
              {filteredFiles.length > 0 && (
                <>
                  {visibleFolders.length > 0 && (
                    <Typography sx={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.1em', color: 'var(--text-disabled)', mb: '8px' }}>Files</Typography>
                  )}
                  <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))', gap: '10px' }}>
                    {filteredFiles.map(file => (
                      <MediaFileCard
                        key={file.id}
                        file={file}
                        selected={selectedFile?.id === file.id}
                        checked={checkedIds.has(file.id)}
                        onClick={() => setSelectedFile(prev => prev?.id === file.id ? null : file)}
                        onCheck={() => toggleCheck(file.id)}
                        avail_status={file.avail_status}
                        avail_sched_enabled={file.avail_sched_enabled}
                        transcoding={file.transcoding}
                        editing={editingFileId === file.id}
                        editingName={editingName}
                        onEditName={setEditingName}
                        onCommitRename={commitRenameFile}
                        onCancelRename={() => setEditingFileId(null)}
                        isDuplicate={editingFileId === file.id && fileNameDuplicate}
                        menuSlot={<ActionMenu fileId={file.id} fileName={file.original_name} onDelete={deleteFile} onDownload={handleDownload} onRename={(id, name) => { setEditingFileId(id); setEditingName(name); }} />}
                        draggable={!file.transcoding}
                        onDragStart={e => {
                          const ids = checkedIds.has(file.id) && checkedIds.size > 1 ? Array.from(checkedIds) : [file.id];
                          setDraggedFileIds(ids);
                          e.dataTransfer.effectAllowed = 'move';
                          e.dataTransfer.setData('text/plain', String(file.id));
                        }}
                        onDragEnd={() => { setDraggedFileIds([]); setDragTargetFolderId(null); }}
                      />
                    ))}
                  </Box>
                </>
              )}
            </>
          ) : (
            /* ─── LIST BODY (tbody only, header is static above) ─── */
            <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
              <colgroup>
                <col style={{ width: 44 }} />
                <col />
                <col style={{ width: 90 }} />
                <col style={{ width: 80 }} />
                <col style={{ width: 90 }} />
                <col style={{ width: 120 }} />
                <col style={{ width: 50 }} />
              </colgroup>
              <tbody>
                {/* Inline new folder row */}
                {inlineNewFolder && (
                  <tr style={{ borderBottom: '1px solid var(--divider)' }}>
                    <td style={{ padding: '10px 10px 10px 18px' }}><Box sx={{ width: 17, height: 17 }} /></td>
                    <td style={{ padding: '8px 12px' }} colSpan={5}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <FolderThumb size={42} />
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                          <Box
                            component="input" autoFocus value={newFolderName}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNewFolderName(e.target.value)}
                            onKeyDown={(e: React.KeyboardEvent) => {
                              if (e.key === 'Enter') createFolder();
                              if (e.key === 'Escape') { setInlineNewFolder(false); setNewFolderName(''); }
                            }}
                            onFocus={(e: React.FocusEvent<HTMLInputElement>) => e.target.select()}
                            sx={{ background: 'var(--glass-bg)', border: `1px solid ${newFolderDuplicate ? '#f87171' : 'var(--accent)'}`, borderRadius: '7px', px: '10px', py: '5px', color: 'var(--text-primary)', fontSize: 14, outline: 'none', minWidth: 180 }}
                          />
                          {newFolderDuplicate
                            ? <Typography sx={{ fontSize: 11, color: '#f87171' }}>Already exists</Typography>
                            : <Typography sx={{ fontSize: 11, color: 'var(--text-disabled)' }}>Enter — save · Esc — cancel</Typography>
                          }
                        </Box>
                        {newFolderLoading && <CircularProgress size={14} sx={{ color: 'var(--accent)' }} />}
                      </Box>
                    </td>
                    <td />
                  </tr>
                )}
                {/* Folder rows */}
                {visibleFolders.map(folder => {
                  const isFolderChecked = checkedFolderIds.has(folder.id);
                  const isFolderDragTarget = dragTargetFolderId === folder.id;
                  return (
                    <tr
                      key={`folder-${folder.id}`}
                      draggable
                      onClick={() => handleFolderOpen(folder.id, folder.name)}
                      style={{ cursor: 'pointer', borderBottom: '1px solid var(--divider)', backgroundColor: isFolderDragTarget ? 'var(--selected-bg)' : isFolderChecked ? 'var(--row-selected)' : 'transparent', outline: isFolderDragTarget ? '2px solid var(--accent)' : 'none', outlineOffset: '-2px' }}
                      onMouseEnter={e => { if (!isFolderChecked && !isFolderDragTarget) (e.currentTarget as HTMLElement).style.backgroundColor = 'var(--row-hover)'; }}
                      onMouseLeave={e => { (e.currentTarget as HTMLElement).style.backgroundColor = isFolderDragTarget ? 'var(--selected-bg)' : isFolderChecked ? 'var(--row-selected)' : 'transparent'; }}
                      onDragStart={e => { setDraggedFolderId(folder.id); e.dataTransfer.effectAllowed = 'move'; e.dataTransfer.setData('pv-folder', String(folder.id)); }}
                      onDragEnd={() => { setDraggedFolderId(null); setDragTargetFolderId(null); }}
                      onDragOver={e => { e.preventDefault(); e.stopPropagation(); if (draggedFolderId !== folder.id) { setDragTargetFolderId(folder.id); (e.currentTarget as HTMLElement).style.backgroundColor = 'var(--selected-bg)'; } }}
                      onDragLeave={e => { setDragTargetFolderId(null); (e.currentTarget as HTMLElement).style.backgroundColor = isFolderChecked ? 'var(--row-selected)' : 'transparent'; }}
                      onDrop={e => {
                        e.preventDefault(); e.stopPropagation(); setDragTargetFolderId(null);
                        (e.currentTarget as HTMLElement).style.backgroundColor = isFolderChecked ? 'var(--row-selected)' : 'transparent';
                        if (e.dataTransfer.types.includes('pv-folder')) {
                          const srcId = Number(e.dataTransfer.getData('pv-folder'));
                          if (srcId && srcId !== folder.id) moveFolderTo(srcId, folder.id);
                          setDraggedFolderId(null);
                        } else { bulkMoveFiles(folder.id); }
                      }}
                    >
                      <td style={{ padding: '10px 10px 10px 18px' }}>
                        <GlassCheckbox checked={isFolderChecked} onChange={() => toggleFolderCheck(folder.id)} />
                      </td>
                      <td style={{ padding: '10px 12px', overflow: 'hidden' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                          <FolderThumb size={42} />
                          <Box sx={{ minWidth: 0 }}>
                            <Typography sx={{ fontSize: 14, fontWeight: 500, color: 'var(--text-primary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{folder.name}</Typography>
                            <Typography sx={{ fontSize: 12, color: 'var(--text-disabled)', mt: '1px' }}>Folder</Typography>
                          </Box>
                        </Box>
                      </td>
                      <td style={{ padding: '10px 12px' }}>
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 12, fontWeight: 600, padding: '2px 9px', borderRadius: 20, backgroundColor: 'rgba(255,255,255,.06)', color: 'var(--text-disabled)' }}>
                          {folder.file_count} items
                        </span>
                      </td>
                      <td style={{ padding: '10px 12px', fontSize: 13, color: 'var(--text-disabled)' }}>—</td>
                      <td style={{ padding: '10px 12px', fontSize: 13, color: 'var(--text-disabled)' }}>—</td>
                      <td style={{ padding: '10px 12px', fontSize: 13, color: 'var(--text-disabled)', whiteSpace: 'nowrap' }}>
                        {folder.created_at ? new Date(folder.created_at).toLocaleDateString() : '—'}
                      </td>
                      <td style={{ padding: '10px 18px 10px 6px' }} onClick={e => e.stopPropagation()}>
                        <FolderActionMenu folderId={folder.id} folderName={folder.name} existingNames={visibleFolders.filter(f2 => f2.id !== folder.id).map(f2 => f2.name)} onDelete={deleteFolder} onRename={renameFolder} />
                      </td>
                    </tr>
                  );
                })}

                {/* File rows */}
                {filteredFiles.map(file => {
                    const chip = availChip(file.avail_status);
                    const isSelected = selectedFile?.id === file.id;
                    const isChecked = checkedIds.has(file.id);
                    return (
                      <tr
                        key={file.id}
                        draggable={!file.transcoding}
                        onClick={() => setSelectedFile(prev => prev?.id === file.id ? null : file)}
                        style={{ cursor: 'pointer', backgroundColor: isSelected ? 'var(--row-selected)' : 'transparent', borderBottom: '1px solid var(--divider)' }}
                        onMouseEnter={e => { if (!isSelected) (e.currentTarget as HTMLElement).style.backgroundColor = 'var(--row-hover)'; }}
                        onMouseLeave={e => { (e.currentTarget as HTMLElement).style.backgroundColor = isSelected ? 'var(--row-selected)' : 'transparent'; }}
                        onDragStart={e => {
                          const ids = checkedIds.has(file.id) && checkedIds.size > 1 ? Array.from(checkedIds) : [file.id];
                          setDraggedFileIds(ids);
                          e.dataTransfer.effectAllowed = 'move';
                          e.dataTransfer.setData('text/plain', String(file.id));
                        }}
                        onDragEnd={() => { setDraggedFileIds([]); setDragTargetFolderId(null); }}
                      >
                        <td style={{ padding: '10px 10px 10px 16px', borderLeft: isSelected ? '2px solid var(--accent)' : '2px solid transparent' }}>
                          <GlassCheckbox checked={isChecked} onChange={() => toggleCheck(file.id)} />
                        </td>
                        <td style={{ padding: '10px 12px', overflow: 'hidden' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                            <Box onClick={e => { e.stopPropagation(); setPlayerFile(file); }} sx={{ cursor: 'zoom-in', flexShrink: 0 }}>
                              <Thumb file={file} size={42} />
                            </Box>
                            <Box sx={{ minWidth: 0, flex: 1 }}>
                              {editingFileId === file.id ? (
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
                                  <Box
                                    component="input"
                                    autoFocus
                                    value={editingName}
                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEditingName(e.target.value)}
                                    onKeyDown={(e: React.KeyboardEvent) => {
                                      e.stopPropagation();
                                      if (e.key === 'Enter' && !fileNameDuplicate) commitRenameFile(file.id, editingName);
                                      if (e.key === 'Escape') setEditingFileId(null);
                                    }}
                                    onBlur={() => setEditingFileId(null)}
                                    onClick={(e: React.MouseEvent) => e.stopPropagation()}
                                    sx={{ width: '100%', boxSizing: 'border-box', background: 'transparent', border: 'none', borderBottom: `1.5px solid ${fileNameDuplicate ? '#f87171' : 'var(--accent)'}`, color: 'var(--text-primary)', fontSize: 14, fontWeight: 500, outline: 'none', pb: '2px' }}
                                  />
                                  {fileNameDuplicate
                                    ? <Typography sx={{ fontSize: 11, color: '#f87171' }}>Already exists</Typography>
                                    : <Typography sx={{ fontSize: 11, color: 'var(--text-disabled)' }}>Enter — save · Esc — cancel</Typography>
                                  }
                                </Box>
                              ) : (
                                <Typography sx={{ fontSize: 14, fontWeight: 500, color: 'var(--text-primary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                  {file.original_name}
                                </Typography>
                              )}
                              <Typography sx={{ fontSize: 12, color: 'var(--text-disabled)', mt: '1px' }}>
                                {isVideo(file.mime_type) ? 'Video' : isImage(file.mime_type) ? 'Image' : file.mime_type}
                                {file.duration_ms ? ` · ${fmtDur(file.duration_ms)}` : ''}
                              </Typography>
                            </Box>
                          </Box>
                        </td>
                        <td style={{ padding: '10px 12px' }}>
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 12, fontWeight: 600, padding: '2px 9px', borderRadius: 20, backgroundColor: chip.bg, color: chip.color }}>
                            <span style={{ width: 5, height: 5, borderRadius: '50%', backgroundColor: chip.dot, flexShrink: 0, boxShadow: `0 0 4px ${chip.dot}` }} />
                            {chip.label}
                          </span>
                        </td>
                        <td style={{ padding: '10px 12px', fontSize: 13, color: 'var(--text-secondary)', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
                          {fmtSizeMB(file.size_bytes)}
                        </td>
                        <td style={{ padding: '10px 12px', fontSize: 13, color: 'var(--text-secondary)', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
                          {file.width && file.height ? `${file.width}×${file.height}` : '—'}
                        </td>
                        <td style={{ padding: '10px 12px', fontSize: 13, color: 'var(--text-disabled)', whiteSpace: 'nowrap' }}>
                          {new Date(file.created_at).toLocaleDateString()}
                        </td>
                        <td style={{ padding: '10px 18px 10px 6px' }}>
                          <ActionMenu fileId={file.id} fileName={file.original_name} onDelete={deleteFile} onDownload={handleDownload} onRename={(id, name) => { setEditingFileId(id); setEditingName(name); }} />
                        </td>
                      </tr>
                    );
                  })}
              </tbody>
            </table>
          )}
        </Box>

        <input ref={fileInputRef} type="file" multiple accept="image/*,video/*" style={{ display: 'none' }} onChange={handleFileInput} />

        {/* ── BOTTOM BAR — always visible, never scrolls ── */}
        <Box sx={{
          flexShrink: 0,
          display: 'flex', alignItems: 'center', gap: '12px',
          px: '20px', py: '11px',
          borderTop: '1px solid var(--divider)',
          ...glassSx, borderRadius: 0,
        }}>
          {(checkedIds.size + checkedFolderIds.size) > 0 ? (
            <>
              <Typography sx={{ fontSize: 13, color: 'var(--text-secondary)', fontWeight: 500 }}>
                {checkedIds.size + checkedFolderIds.size} selected
              </Typography>
              <Box sx={{ flex: 1 }} />
              {/* Move to folder button — only for file selections */}
              {checkedIds.size > 0 && (
                <Box sx={{ position: 'relative' }}>
                  <Box
                    component="button"
                    ref={moveBtnRef as any}
                    onClick={() => setMovePanelOpen(v => !v)}
                    sx={{ display: 'flex', alignItems: 'center', gap: '6px', borderRadius: '8px', px: '12px', py: '6px', ...glassSx, color: 'var(--text-secondary)', fontSize: 13, cursor: 'pointer', '&:hover': { color: 'var(--text-primary)' } }}
                  >
                    <svg width={13} height={13} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"/><polyline points="12 11 12 17"/><polyline points="9 14 12 17 15 14"/></svg>
                    Move to…
                  </Box>
                  {movePanelOpen && (
                    <Box sx={{ position: 'absolute', bottom: 'calc(100% + 8px)', left: 0, minWidth: 220, maxHeight: 280, overflowY: 'auto', borderRadius: '12px', border: '1px solid var(--glass-border)', background: 'var(--glass-bg)', backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)', boxShadow: '0 12px 40px rgba(0,0,0,.3)', zIndex: 500 }}>
                      <Typography sx={{ px: '14px', py: '10px', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.06em', color: 'var(--text-disabled)', borderBottom: '1px solid var(--glass-border)' }}>
                        Move {checkedIds.size} {checkedIds.size === 1 ? 'file' : 'files'} to
                      </Typography>
                      {/* Root level option */}
                      {selectedFolderId !== null && (
                        <Box onClick={() => bulkMoveFiles(null)} sx={{ display: 'flex', alignItems: 'center', gap: '10px', px: '14px', py: '9px', cursor: 'pointer', borderBottom: '1px solid var(--divider)', '&:hover': { bgcolor: 'var(--glass-hover)' } }}>
                          <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>
                          <Typography sx={{ fontSize: 13, color: 'var(--text-primary)' }}>Root (All files)</Typography>
                        </Box>
                      )}
                      {folders.filter(f => f.id !== selectedFolderId && f.parent_id === null).length === 0 && folders.filter(f => f.id !== selectedFolderId).length === 0 && (
                        <Typography sx={{ px: '14px', py: '12px', fontSize: 13, color: 'var(--text-disabled)' }}>No folders available</Typography>
                      )}
                      {folders.filter(f => f.id !== selectedFolderId).map(f => (
                        <Box key={f.id} onClick={() => bulkMoveFiles(f.id)} sx={{ display: 'flex', alignItems: 'center', gap: '10px', px: '14px', py: '9px', cursor: 'pointer', borderBottom: '1px solid var(--divider)', '&:hover': { bgcolor: 'var(--glass-hover)' }, '&:last-child': { borderBottom: 'none' } }}>
                          <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="var(--accent)" strokeWidth={1.8} strokeLinecap="round"><path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"/></svg>
                          <Typography sx={{ fontSize: 13, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.name}</Typography>
                        </Box>
                      ))}
                    </Box>
                  )}
                </Box>
              )}
              <Box component="button" onClick={() => { setCheckedIds(new Set()); setCheckedFolderIds(new Set()); setMovePanelOpen(false); }} sx={{ display: 'flex', alignItems: 'center', gap: '5px', ...glassSx, borderRadius: '8px', px: '12px', py: '6px', color: 'var(--text-secondary)', fontSize: 13, cursor: 'pointer', '&:hover': { color: 'var(--text-primary)' } }}>
                Clear
              </Box>
              <Box component="button" onClick={deleteSelected} sx={{ display: 'flex', alignItems: 'center', gap: '6px', borderRadius: '8px', px: '14px', py: '6px', bgcolor: 'rgba(248,113,113,.12)', border: '1px solid rgba(248,113,113,.3)', color: '#f87171', fontSize: 13, fontWeight: 600, cursor: 'pointer', '&:hover': { bgcolor: 'rgba(248,113,113,.22)', borderColor: '#f87171', color: '#fca5a5' } }}>
                <IcoTrash /> Delete {checkedIds.size + checkedFolderIds.size}
              </Box>
            </>
          ) : (
            <Typography sx={{ fontSize: 12, color: 'var(--text-disabled)' }}>
              {totalItems} {totalItems === 1 ? 'item' : 'items'} · Drag & drop files to folders to move them
            </Typography>
          )}
        </Box>
        </Box>

      </Box>

      </Box> {/* end LEFT ISLAND */}

      {/* ── RIGHT ISLAND: detail panel ── */}
      <Box sx={{
        width: 300, flexShrink: 0,
        display: { xs: 'none', md: 'flex' }, flexDirection: 'column', overflow: 'hidden',
        background: 'var(--glass-bg)', backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)',
        border: '1px solid var(--glass-border)', borderRadius: '14px',
      }}>
        <DetailPanel
          file={selectedFile}
          t={t}
          onDelete={deleteFile}
          onEdit={setEditorFileId}
          onFileUpdated={(id, patch) => {
            setFiles(prev => prev.map(f => f.id === id ? { ...f, ...patch } : f));
            setSelectedFile(prev => prev?.id === id ? { ...prev, ...patch } : prev);
          }}
        />
      </Box>

      {/* ── VIDEO / IMAGE PLAYER ── */}
      {playerFile && <MediaPlayer file={playerFile} onClose={() => setPlayerFile(null)} />}

      {/* ── IMAGE EDITOR MODAL ── */}
      {editorFileId !== null && createPortal(
        <Box onClick={() => setEditorFileId(null)} sx={{ position: 'fixed', inset: 0, zIndex: 9500, bgcolor: 'rgba(0,0,0,.7)', backdropFilter: 'blur(6px)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Box onClick={e => e.stopPropagation()} sx={{ width: '80vw', height: '80vh', borderRadius: '14px', overflow: 'hidden', boxShadow: '0 32px 96px rgba(0,0,0,.9)' }}>
            <ImageEditorPage fileId={editorFileId} onClose={() => setEditorFileId(null)} />
          </Box>
        </Box>,
        document.body,
      )}

      {/* ── CONFLICT MODAL ── */}
      {conflictItems && (
        <ConflictModal
          items={conflictItems}
          existingNames={files.map(f => f.original_name)}
          totalFiles={pendingUpload?.files.length ?? 0}
          onResolve={proceedWithUpload}
          onCancel={() => { setConflictItems(null); setPendingUpload(null); }}
        />
      )}

      {/* ── DOWNLOAD PROGRESS TOAST ── */}
      {sizeError.length > 0 && (
        <Box sx={{
          position: 'fixed', bottom: sizeError.length > 0 && dlState ? 130 : 24, right: 24, zIndex: 9999,
          width: 300, borderRadius: '12px', p: '14px 16px',
          background: 'rgba(30,10,10,.92)', backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)',
          border: '1px solid rgba(248,113,113,.3)',
          boxShadow: '0 8px 32px rgba(0,0,0,.5)',
          display: 'flex', flexDirection: 'column', gap: '6px',
          animation: 'fadeSlideUp .2s ease',
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#f87171', flex: 1 }}>
              Too large (max 100 MB)
            </Typography>
          </Box>
          {sizeError.map((name, i) => (
            <Typography key={i} sx={{ fontSize: 11, color: 'rgba(248,113,113,.7)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {name}
            </Typography>
          ))}
        </Box>
      )}

      {dlState && (
        <Box sx={{
          position: 'fixed', bottom: 24, right: 24, zIndex: 9999,
          width: 280, borderRadius: '12px', p: '14px 16px',
          ...glassSx,
          boxShadow: '0 8px 32px rgba(0,0,0,.5)',
          display: 'flex', flexDirection: 'column', gap: '8px',
          animation: 'fadeSlideUp .2s ease',
          '@keyframes fadeSlideUp': {
            from: { opacity: 0, transform: 'translateY(10px)' },
            to:   { opacity: 1, transform: 'translateY(0)' },
          },
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Box sx={{ fontSize: 15 }}>{dlState.done ? '✓' : '↓'}</Box>
            <Typography sx={{
              fontSize: 13, fontWeight: 500, color: 'var(--text-primary)',
              flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            }}>
              {dlState.done ? 'Downloaded' : 'Downloading…'}
            </Typography>
            {!dlState.done && dlState.total > 0 && (
              <Typography sx={{ fontSize: 12, color: 'var(--text-disabled)', flexShrink: 0 }}>
                {dlState.pct}%
              </Typography>
            )}
          </Box>
          <Typography sx={{
            fontSize: 11, color: 'var(--text-disabled)',
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }}>
            {dlState.name}
          </Typography>
          {/* progress bar */}
          <Box sx={{ height: 3, borderRadius: 4, bgcolor: 'rgba(255,255,255,.08)', overflow: 'hidden' }}>
            {dlState.total > 0 ? (
              <Box sx={{
                height: '100%',
                width: `${dlState.pct}%`,
                bgcolor: dlState.done ? '#34d399' : 'var(--accent)',
                borderRadius: 4,
                transition: 'width .15s ease',
              }} />
            ) : (
              <Box sx={{
                height: '100%', width: '40%',
                bgcolor: 'var(--accent)', borderRadius: 4,
                animation: 'indeterminate 1.2s ease infinite',
                '@keyframes indeterminate': {
                  '0%':   { transform: 'translateX(-100%)' },
                  '100%': { transform: 'translateX(350%)' },
                },
              }} />
            )}
          </Box>
        </Box>
      )}

      {deleteMsg && (
        <Box sx={{
          position: 'fixed', bottom: dlState ? 140 : (sizeError.length > 0 ? 130 : 24), left: '50%',
          transform: 'translateX(-50%)', zIndex: 9999,
          maxWidth: 420, width: 'max-content', borderRadius: '10px', px: '18px', py: '10px',
          background: 'rgba(30,10,10,.92)', backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)',
          border: '1px solid rgba(248,113,113,.3)', boxShadow: '0 8px 32px rgba(0,0,0,.5)',
          display: 'flex', alignItems: 'center', gap: '8px',
          animation: 'fadeSlideUp .2s ease',
        }}>
          <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#f87171' }}>Delete failed:</Typography>
          <Typography sx={{ fontSize: 13, color: 'rgba(248,113,113,.8)' }}>{deleteMsg}</Typography>
        </Box>
      )}

      {/* File usage blocking modal */}
      {usageModal && (
        <Box sx={{
          position: 'fixed', inset: 0, zIndex: 2000,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          background: 'rgba(0,0,0,.6)', backdropFilter: 'blur(8px)',
        }} onClick={() => setUsageModal(null)}>
          <Box onClick={e => e.stopPropagation()} sx={{
            background: 'var(--glass-bg)', border: '1px solid var(--glass-border)',
            borderRadius: '16px', backdropFilter: 'blur(24px)',
            maxWidth: 460, width: '90%',
            boxShadow: '0 32px 64px rgba(0,0,0,.5)',
            overflow: 'hidden',
          }}>
            {/* Header */}
            <Box sx={{ px: '24px', pt: '22px', pb: '16px', borderBottom: '1px solid var(--glass-border)' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px', mb: '4px' }}>
                <Box sx={{ fontSize: 18, lineHeight: 1 }}>{usageModal.transcoding ? '⏳' : '🚫'}</Box>
                <Box sx={{ fontSize: 15, fontWeight: 700, color: 'var(--text-primary)' }}>
                  {usageModal.transcoding ? t('media.transcoding') : t('media.usedInTitle')}
                </Box>
              </Box>
              <Box sx={{ fontSize: 13, color: 'var(--text-secondary)', pl: '28px' }}>
                <Box component="span" sx={{ fontWeight: 600, color: 'var(--text-primary)' }}>
                  {usageModal.fileName}
                </Box>
                {' '}{usageModal.transcoding
                  ? t('media.transcodingDesc')
                  : t('media.usedInDesc')}
              </Box>
              {usageModal.blockedCount && usageModal.blockedCount > 1 && (
                <Box sx={{ mt: '8px', pl: '28px', fontSize: 12, color: '#f87171' }}>
                  {t('media.blockedCount', { count: usageModal.blockedCount })}
                </Box>
              )}
            </Box>

            {/* Body */}
            <Box sx={{ px: '24px', py: '16px', display: 'flex', flexDirection: 'column', gap: '10px', maxHeight: '40vh', overflowY: 'auto' }}>
              {!usageModal.transcoding && usageModal.playlists.length > 0 && (
                <Box>
                  <Box sx={{ fontSize: 10, fontWeight: 700, color: 'var(--text-disabled)', textTransform: 'uppercase', letterSpacing: '.08em', mb: '6px' }}>
                    {t('media.usedInPlaylists')}
                  </Box>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                    {usageModal.playlists.map((p, i) => (
                      <Box key={i} sx={{
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        px: '12px', py: '8px', borderRadius: '8px',
                        background: 'var(--glass-hover)', border: '1px solid var(--glass-border)',
                      }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="var(--accent-light)" strokeWidth={2} strokeLinecap="round"><rect x="2" y="2" width="20" height="20" rx="3"/><polygon points="10 8 16 12 10 16 10 8" fill="var(--accent-light)" stroke="none"/></svg>
                          <Box sx={{ fontSize: 13, color: 'var(--text-primary)', fontWeight: 500 }}>{p.playlistName}</Box>
                        </Box>
                        <Box sx={{ fontSize: 11, color: 'var(--text-secondary)', background: 'var(--glass-bg)', px: '8px', py: '2px', borderRadius: '20px', border: '1px solid var(--glass-border)', whiteSpace: 'nowrap' }}>
                          #{p.position}
                        </Box>
                      </Box>
                    ))}
                  </Box>
                </Box>
              )}

              {!usageModal.transcoding && (usageModal.schedules?.length ?? 0) > 0 && (
                <Box>
                  <Box sx={{ fontSize: 10, fontWeight: 700, color: 'var(--text-disabled)', textTransform: 'uppercase', letterSpacing: '.08em', mb: '6px' }}>
                    {t('media.usedInSchedules')}
                  </Box>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                    {usageModal.schedules!.map((sc, i) => (
                      <Box key={i} sx={{
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        px: '12px', py: '8px', borderRadius: '8px',
                        background: 'var(--glass-hover)', border: '1px solid var(--glass-border)',
                      }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="var(--accent-light)" strokeWidth={2} strokeLinecap="round"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
                          <Box sx={{ fontSize: 13, color: 'var(--text-primary)', fontWeight: 500 }}>{sc.scheduleName}</Box>
                        </Box>
                        <Box sx={{ fontSize: 11, color: 'var(--text-secondary)', background: 'var(--glass-bg)', px: '8px', py: '2px', borderRadius: '20px', border: '1px solid var(--glass-border)', whiteSpace: 'nowrap' }}>
                          {sc.eventName}
                        </Box>
                      </Box>
                    ))}
                  </Box>
                </Box>
              )}

              {!usageModal.transcoding && usageModal.screens.length > 0 && (
                <Box>
                  <Box sx={{ fontSize: 10, fontWeight: 700, color: 'var(--text-disabled)', textTransform: 'uppercase', letterSpacing: '.08em', mb: '6px' }}>
                    {t('media.usedInScreens')}
                  </Box>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                    {usageModal.screens.map((s, i) => (
                      <Box key={i} sx={{
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        px: '12px', py: '8px', borderRadius: '8px',
                        background: 'var(--glass-hover)', border: '1px solid var(--glass-border)',
                      }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="var(--accent-light)" strokeWidth={2} strokeLinecap="round"><rect x="2" y="3" width="20" height="14" rx="2"/><line x1="8" y1="21" x2="16" y2="21"/><line x1="12" y1="17" x2="12" y2="21"/></svg>
                          <Box sx={{ fontSize: 13, color: 'var(--text-primary)', fontWeight: 500 }}>{s.displayName || s.deviceId}</Box>
                        </Box>
                        <Box sx={{ fontSize: 11, color: 'var(--text-secondary)', background: 'var(--glass-bg)', px: '8px', py: '2px', borderRadius: '20px', border: '1px solid var(--glass-border)', whiteSpace: 'nowrap' }}>
                          {t('media.defaultContent')}
                        </Box>
                      </Box>
                    ))}
                  </Box>
                </Box>
              )}

              {!usageModal.transcoding && (
                <Box sx={{ fontSize: 12, color: 'var(--text-primary)', background: 'rgba(245,158,11,.12)', border: '1px solid rgba(245,158,11,.3)', borderRadius: '8px', px: '12px', py: '8px' }}>
                  {t('media.usedInHint')}
                </Box>
              )}
            </Box>

            {/* Footer */}
            <Box sx={{ px: '24px', py: '14px', borderTop: '1px solid var(--glass-border)', display: 'flex', justifyContent: 'flex-end' }}>
              <Box
                component="button"
                onClick={() => setUsageModal(null)}
                sx={{
                  px: '20px', py: '8px', borderRadius: '8px', border: 'none', cursor: 'pointer',
                  background: 'var(--accent)', color: '#fff', fontSize: 13, fontWeight: 600,
                  '&:hover': { filter: 'brightness(1.12)' }, transition: 'filter .15s',
                }}
              >
                OK
              </Box>
            </Box>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default MediaPage;
